import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, TrashIcon, PencilIcon, CheckIcon, EllipsisVerticalIcon, LockClosedIcon, LockOpenIcon, Cog6ToothIcon, UserGroupIcon, DocumentTextIcon, ArrowPathIcon, BellIcon, Squares2X2Icon, PlusIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Etapa, Card, CampoConfig, Departamento, Colaborador, Flow, CardTemplate, CardTemplateRecorrencia, FornecedorCotacao } from '../../types';
import { checkEditorFlowPermissions, checkEditorFieldPermissions } from '../../types';
import SeletorIntegracao from '../Integracoes/SeletorIntegracao';
import { apiUrl } from '../../utils/apiBase';
import SeletorCamposCotacao from '../Cotacao/SeletorCamposCotacao';

// Opções de data dinâmica usadas em campos de data dentro de modelos com recorrência.
// O valor é gravado como placeholder ({{hoje}}, {{hoje+Nd}}, {{dia_mes:N}}, {{ultimo_dia_mes}})
// e resolvido pelo servidor no momento em que o card recorrente é criado.
type DataDinamicaModo = 'fixa' | 'hoje' | 'hoje_mais' | 'dia_mes' | 'dia_mes_corrente' | 'ultimo_dia';

const parseDataDinamica = (valor: any): { modo: DataDinamicaModo; dias: number; diaMes: number; dataFixa: string } => {
  const v = typeof valor === 'string' ? valor : '';
  if (v === '{{hoje}}') return { modo: 'hoje', dias: 7, diaMes: 1, dataFixa: '' };
  if (v === '{{ultimo_dia_mes}}') return { modo: 'ultimo_dia', dias: 7, diaMes: 1, dataFixa: '' };
  const mMais = v.match(/^\{\{hoje\+(\d+)d\}\}$/);
  if (mMais) return { modo: 'hoje_mais', dias: parseInt(mMais[1], 10), diaMes: 1, dataFixa: '' };
  const mDiaMesCorr = v.match(/^\{\{dia_mes_corrente:(\d{1,2})\}\}$/);
  if (mDiaMesCorr) return { modo: 'dia_mes_corrente', dias: 7, diaMes: parseInt(mDiaMesCorr[1], 10), dataFixa: '' };
  const mDiaMes = v.match(/^\{\{dia_mes:(\d{1,2})\}\}$/);
  if (mDiaMes) return { modo: 'dia_mes', dias: 7, diaMes: parseInt(mDiaMes[1], 10), dataFixa: '' };
  return { modo: 'fixa', dias: 7, diaMes: 1, dataFixa: v };
};

// Seletor de data dinâmica (totalmente derivado do valor, sem estado interno).
function CampoDataDinamica({ valor, onChange }: { valor: any; onChange: (v: any) => void }) {
  const { modo, dias, diaMes, dataFixa } = parseDataDinamica(valor);
  return (
    <div className="space-y-1.5">
      <select
        value={modo}
        onChange={(e) => {
          const m = e.target.value as DataDinamicaModo;
          if (m === 'fixa') onChange(dataFixa || '');
          else if (m === 'hoje') onChange('{{hoje}}');
          else if (m === 'hoje_mais') onChange(`{{hoje+${dias || 7}d}}`);
          else if (m === 'dia_mes') onChange(`{{dia_mes:${diaMes || 1}}}`);
          else if (m === 'dia_mes_corrente') onChange(`{{dia_mes_corrente:${diaMes || 1}}}`);
          else if (m === 'ultimo_dia') onChange('{{ultimo_dia_mes}}');
        }}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      >
        <option value="fixa">Data fixa</option>
        <option value="hoje">No dia da criação (hoje)</option>
        <option value="hoje_mais">Hoje + N dias</option>
        <option value="dia_mes_corrente">Dia X deste mês</option>
        <option value="dia_mes">Próximo dia X do mês</option>
        <option value="ultimo_dia">Último dia do mês</option>
      </select>
      {modo === 'fixa' && (
        <input
          type="date"
          value={dataFixa}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      )}
      {modo === 'hoje_mais' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={dias}
            onChange={(e) => onChange(`{{hoje+${Math.max(0, parseInt(e.target.value || '0', 10))}d}}`)}
            className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <span className="text-[11px] text-gray-500">dia(s) após a criação</span>
        </div>
      )}
      {modo === 'dia_mes_corrente' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={31}
            value={diaMes}
            onChange={(e) => onChange(`{{dia_mes_corrente:${Math.min(31, Math.max(1, parseInt(e.target.value || '1', 10)))}}}`)}
            className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <span className="text-[11px] text-gray-500">dia do mês corrente (vencimento)</span>
        </div>
      )}
      {modo === 'dia_mes' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={31}
            value={diaMes}
            onChange={(e) => onChange(`{{dia_mes:${Math.min(31, Math.max(1, parseInt(e.target.value || '1', 10)))}}}`)}
            className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <span className="text-[11px] text-gray-500">próximo dia do mês</span>
        </div>
      )}
    </div>
  );
}

// Helper para exibir nomes legíveis dos tipos de campo
const getTipoNomeAmigavel = (tipo: CampoConfig['tipo']): string => {
  const tipos: Record<CampoConfig['tipo'], string> = {
    'texto': '📝 Texto',
    'texto_longo': '📄 Texto Longo',
    'numero': '🔢 Número (apenas números)',
    'data': '📅 Data e Horário',
    'data_vencimento': '⏰ Data de Vencimento',
    'lista': '📋 Lista de Seleção',
    'checkbox': '✅ Checkbox (múltipla)',
    'checkbox_single': '🔘 Checkbox (única seleção)',
    'checkbox_multi': '✅ Checkbox (múltipla seleção)',
    'progresso': '☑️ Lista de Tarefas',
    'valor': '💰 Valor em R$',
    'anexo': '📎 Anexo',
    'justificativa_checkbox': '💬 Justificativa de Checkbox',
    'telefone': '📞 Telefone',
    'rotas': '✈️ Rotas de Viagem',
    'rota_terrestre': '🚗 Rota Terrestre',
    'viajantes': '🧳 Viajantes (nome + CPF)',
    'quadro_voos': '🛫 Quadro demonstrativo de voos',
    'documento': '📄 Documento',
    'texto_informativo': '🏷️ Texto Informativo',
    'parcelas': '💳 Parcelas de pagamento',
    'itens': '🧾 Itens da solicitação (por fornecedor)',
    'cotacoes': '📊 Mapa de cotação (preço por item)',
    'responsavel': '👤 Responsável (pessoas do fluxo)',
    'nd_vinculada': '🧾 ND vinculada (verba do fornecedor)'
  };
  return tipos[tipo] || tipo;
};

interface ConfiguracaoEtapaProps {
  etapa: Etapa;
  flow: Flow;
  cards: Card[];
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  onSave: (etapa: Etapa) => void;
  onClose: () => void;
  onDelete: (etapaId: string) => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
    configurarEtapas?: any; // granular section-level permission from permissoes_flow
  };
}


interface EditorOpcoesCampoProps {
  opcoes: string[];
  coresOpcoes?: Record<string, string>;
  /** progresso é lista de tarefas: não tem cor de badge */
  mostrarCores: boolean;
  onChange: (opcoes: string[], coresOpcoes: Record<string, string>) => void;
}

/**
 * Lista de opções de um campo (lista / checkbox / progresso / parcelas).
 *
 * O texto é editável no lugar e dá para reordenar. Antes só dava para adicionar
 * e apagar: corrigir um acento no fim da lista obrigava a apagar tudo abaixo e
 * redigitar na ordem certa.
 */
const EditorOpcoesCampo = ({ opcoes, coresOpcoes, mostrarCores, onChange }: EditorOpcoesCampoProps) => {
  const cores = coresOpcoes || {};

  const renomear = (idx: number, novoNome: string) => {
    const antigo = opcoes[idx];
    const novasOpcoes = opcoes.map((o, i) => (i === idx ? novoNome : o));
    // A cor é indexada pelo texto da opção, então renomear sem mover a chave
    // deixaria o badge órfão e a opção nova sem cor.
    const novasCores = { ...cores };
    if (antigo in novasCores) {
      const cor = novasCores[antigo];
      delete novasCores[antigo];
      if (novoNome) novasCores[novoNome] = cor;
    }
    onChange(novasOpcoes, novasCores);
  };

  const mover = (idx: number, direcao: -1 | 1) => {
    const destino = idx + direcao;
    if (destino < 0 || destino >= opcoes.length) return;
    const novasOpcoes = [...opcoes];
    [novasOpcoes[idx], novasOpcoes[destino]] = [novasOpcoes[destino], novasOpcoes[idx]];
    onChange(novasOpcoes, cores);
  };

  const remover = (idx: number) => {
    const alvo = opcoes[idx];
    const novasCores = { ...cores };
    delete novasCores[alvo];
    onChange(opcoes.filter((_, i) => i !== idx), novasCores);
  };

  return (
    <div className="bg-gray-50 p-2 rounded-lg border border-gray-200 max-h-56 overflow-y-auto">
      {mostrarCores && (
        <p className="text-[9px] text-gray-400 mb-1 px-1">💡 Defina uma cor para cada opção (exibida como badge no mini card)</p>
      )}
      {opcoes.map((opcao, idx) => {
        const corAtual = cores[opcao] || '';
        return (
          <div key={idx} className="flex items-center gap-1.5 p-1 hover:bg-white rounded text-xs">
            <div className="flex flex-col flex-shrink-0">
              <button
                type="button"
                onClick={() => mover(idx, -1)}
                disabled={idx === 0}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed leading-none text-[9px]"
                title="Subir"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => mover(idx, 1)}
                disabled={idx === opcoes.length - 1}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed leading-none text-[9px]"
                title="Descer"
              >
                ▼
              </button>
            </div>
            <input
              value={opcao}
              onChange={(e) => renomear(idx, e.target.value)}
              className="flex-1 min-w-0 px-2 py-1 text-xs border border-transparent rounded bg-white/70 hover:border-gray-300 focus:border-gray-400 focus:bg-white focus:outline-none"
              style={corAtual ? { borderLeft: `3px solid ${corAtual}` } : undefined}
            />
            {mostrarCores && (
              <>
                <input
                  type="color"
                  value={corAtual || '#6b7280'}
                  onChange={(e) => onChange(opcoes, { ...cores, [opcao]: e.target.value })}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer p-0 flex-shrink-0"
                  title="Cor para exibir no mini card"
                />
                {corAtual && (
                  <button
                    type="button"
                    onClick={() => {
                      const novasCores = { ...cores };
                      delete novasCores[opcao];
                      onChange(opcoes, novasCores);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-[10px] flex-shrink-0"
                    title="Remover cor"
                  >
                    ✕
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => remover(idx)}
              type="button"
              className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"
              title="Remover opção"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// Função para gerar IDs estáveis e únicos (prefixo 'campo_')
const gerarIdCampo = () => {
  return 'campo_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2,8);
};

// Componente Sortable para cada campo (drag handle sempre visível)
function SortableItem({ campo, children }: { campo: CampoConfig; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: campo.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className={`p-3 bg-white/70 rounded-lg border border-white/20 space-y-3 ${isDragging ? 'shadow-lg ring-2 ring-indigo-500' : ''}`}>        
        <div className="flex items-start gap-2">
          <div
            {...listeners}
            className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>
          <div className="flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

export const ConfiguracaoEtapa = ({ 
  etapa, 
  flow,
  cards, 
  departamentos,
  colaboradores,
  onSave, 
  onClose, 
  onDelete,
  userPermissions
}: ConfiguracaoEtapaProps) => {
  const [nomeEtapa, setNomeEtapa] = useState(etapa.nome);
  const camposInitRef = useRef<CampoConfig[]>(
    (etapa.campos || []).map(c => ({ ...c, id: String(c.id) }))
  );
  const [campos, setCampos] = useState<CampoConfig[]>(camposInitRef.current);
  const [departamentoId, setDepartamentoId] = useState(etapa.departamentoId || '');
  const [participantes, setParticipantes] = useState<string[]>(etapa.participantes || []);
  
  const [permiteMoverPara, setPermiteMoverPara] = useState<string[]>(etapa.permiteMoverPara || []);
  const [permiteCriarCards, setPermiteCriarCards] = useState<boolean>(etapa.permiteCriarCards ?? true);
  // 📎 Etapa de reprovação que devolve anexos para o solicitante do portal
  const [solicitarReenvioAnexos, setSolicitarReenvioAnexos] = useState<boolean>(etapa.solicitarReenvioAnexos ?? false);
  const [etapaRetornoReenvioId, setEtapaRetornoReenvioId] = useState<string>(etapa.etapaRetornoReenvioId || '');
  const [etiquetaReenvioNome, setEtiquetaReenvioNome] = useState<string>(etapa.etiquetaReenvioNome ?? 'Reanálise');
  const [etiquetaReenvioCor, setEtiquetaReenvioCor] = useState<string>(etapa.etiquetaReenvioCor || '#F59E0B');
  const [responsavelPadraoId, setResponsavelPadraoId] = useState<string>(etapa.responsavelPadraoId || '');
  
  const [responsavelQuemMoveu, setResponsavelQuemMoveu] = useState<boolean>(!!etapa.responsavelQuemMoveu);
  const [regrasResponsavelTipo, setRegrasResponsavelTipo] = useState<'criador' | 'responsavel_anterior' | 'campo' | ''>
    (etapa.regrasResponsavel?.tipo || '');
  const [regrasResponsavelCondicoes, setRegrasResponsavelCondicoes] = useState<Array<{quando: string; atribuirPara: string}>>(
    etapa.regrasResponsavel?.condicoes || []
  );
  
  const [tempoMaximoHoras, setTempoMaximoHoras] = useState<number>(etapa.tempoMaximoHoras || 0);
  const [slaIgnorarFimDeSemana, setSlaIgnorarFimDeSemana] = useState<boolean>(etapa.slaIgnorarFimDeSemana ?? false);
  const [mostrarCampoTitulo, setMostrarCampoTitulo] = useState<boolean>(etapa.mostrarCampoTitulo !== false); // default true
  
  const [limparCamposAtivo, setLimparCamposAtivo] = useState<boolean>(etapa.limparCamposAoRetornar?.ativo ?? false);
  const [camposParaLimparAoRetornar, setCamposParaLimparAoRetornar] = useState<string[]>(etapa.limparCamposAoRetornar?.campos || []);
  
  const [notificacoesEmailHabilitado, setNotificacoesEmailHabilitado] = useState<boolean>(
    etapa.notificacoesEmail?.habilitado ?? false
  );
  const [notificacoesEmailColaboradores, setNotificacoesEmailColaboradores] = useState<{
    [colaboradorId: string]: { receberNotificacao: boolean };
  }>(etapa.notificacoesEmail?.colaboradores || {});
  
  const [notificacoesWhatsappHabilitado, setNotificacoesWhatsappHabilitado] = useState<boolean>(
    etapa.notificacoesWhatsapp?.habilitado ?? false
  );
  const [notificacoesWhatsappColaboradores, setNotificacoesWhatsappColaboradores] = useState<{
    [colaboradorId: string]: { receberNotificacao: boolean };
  }>(etapa.notificacoesWhatsapp?.colaboradores || {});
  
  const [alertaSlaWhatsappHabilitado, setAlertaSlaWhatsappHabilitado] = useState<boolean>(
    etapa.alertaSlaWhatsapp?.habilitado ?? false
  );

  // Escalonamento de SLA por e-mail (2x, 3x, 4x... o prazo da etapa)
  const [escalonamentoSlaHabilitado, setEscalonamentoSlaHabilitado] = useState<boolean>(
    etapa.escalonamentoSla?.habilitado ?? false
  );
  const [escalonamentoDestinatarios, setEscalonamentoDestinatarios] = useState<string[]>(
    etapa.escalonamentoSla?.destinatariosExtras || []
  );
  const [escalonamentoEmails, setEscalonamentoEmails] = useState<string[]>(
    etapa.escalonamentoSla?.emailsExtras || []
  );
  const [escalonamentoEmailNovo, setEscalonamentoEmailNovo] = useState('');
  const [escalonamentoBusca, setEscalonamentoBusca] = useState('');
  
  const [camposWhatsappHabilitado, setCamposWhatsappHabilitado] = useState<boolean>(
    etapa.camposWhatsapp?.habilitado ?? false
  );
  const [camposWhatsappSelecionados, setCamposWhatsappSelecionados] = useState<string[]>(
    etapa.camposWhatsapp?.campos || []
  );
  const [camposWhatsappColaboradores, setCamposWhatsappColaboradores] = useState<{
    [colaboradorId: string]: { receberNotificacao: boolean };
  }>(etapa.camposWhatsapp?.colaboradores || {});
  
  const [documentosHabilitados, setDocumentosHabilitados] = useState<string[]>(etapa.documentosHabilitados || []);
  const [templatesDocumentos, setTemplatesDocumentos] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  const [camposVisiveisTmp, setCamposVisiveisTmp] = useState<string[]>(etapa.camposVisiveisNoCard ?? []);
  const [camposTravadosTmp, setCamposTravadosTmp] = useState<string[]>(etapa.camposTravados ?? []);
  const [camposOutrasEtapas, setCamposOutrasEtapas] = useState<string[]>(etapa.camposOutrasEtapas ?? []);

  // 💰 Cotação: campos que o pedido leva por padrão quando sai desta etapa
  const [cotacaoContexto, setCotacaoContexto] = useState<string[]>(etapa.cotacaoCampos?.contexto || []);
  const [cotacaoResposta, setCotacaoResposta] = useState<string[]>(etapa.cotacaoCampos?.resposta || []);
  const [cotacaoAberto, setCotacaoAberto] = useState(false);
  const [cotacaoFornecedores, setCotacaoFornecedores] = useState<FornecedorCotacao[]>(
    etapa.cotacaoFornecedores || []
  );

  // 🔪 Fatiamento: quebrar um card em cards novos escolhendo os campos na hora
  const [fatiarHabilitado, setFatiarHabilitado] = useState<boolean>(etapa.configuracaoFatiamento?.habilitado ?? false);
  const [fatiarRotulo, setFatiarRotulo] = useState<string>(etapa.configuracaoFatiamento?.rotulo || '');
  const [fatiarCamposFatiaveis, setFatiarCamposFatiaveis] = useState<string[]>(etapa.configuracaoFatiamento?.camposFatiaveis || []);
  
  const [expandirParticipantes, setExpandirParticipantes] = useState(false);
  const [expandirCampos, setExpandirCampos] = useState(false);
  const [expandirNovoCampo, setExpandirNovoCampo] = useState(false);
  const [camposConfiguradosAberto, setCamposConfiguradosAberto] = useState(false);
  const [camposVisiveisAberto, setCamposVisiveisAberto] = useState(false);
  const [documentosAberto, setDocumentosAberto] = useState(false);
  
  const [responsavelPadraoAberto, setResponsavelPadraoAberto] = useState(!!etapa.responsavelPadraoId);
  const [regrasCondicionaisAberto, setRegrasCondicionaisAberto] = useState(!!etapa.regrasResponsavel?.tipo);
  
  const [permitirCriacaoAberto, setPermitirCriacaoAberto] = useState(false);
  const [movimentacaoAberto, setMovimentacaoAberto] = useState(permiteMoverPara.length > 0);
  const [slaAberto, setSlaAberto] = useState(!!etapa.tempoMaximoHoras && etapa.tempoMaximoHoras > 0);
  const [limparCamposAberto, setLimparCamposAberto] = useState(!!etapa.limparCamposAoRetornar?.ativo);
  
  // Aba ativa na navegação lateral
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'participantes' | 'campos' | 'fluxo' | 'notificacoes' | 'templates'>('geral');

  // Cards Personalizados (modelos) desta etapa
  const [cardsPersonalizados, setCardsPersonalizados] = useState<CardTemplate[]>(etapa.cardsPersonalizados || []);
  const [templateEditandoId, setTemplateEditandoId] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState<CardTemplate | null>(null);

  // Integração GIX (somente localhost): campos onde renderizar badge de status e botão Criar OC
  const [gixBadgeCampoId, setGixBadgeCampoId] = useState<string>(etapa.gixBadgeCampoId || '');
  const [gixCriarOcCampoId, setGixCriarOcCampoId] = useState<string>(etapa.gixCriarOcCampoId || '');
  const [gixAprovacaoCampoId, setGixAprovacaoCampoId] = useState<string>(etapa.gixAprovacaoCampoId || '');
  const [gixAutoAprovarMov96, setGixAutoAprovarMov96] = useState<boolean>(!!etapa.gixAutoAprovarMov96);
  const [gixLancarNfMov96, setGixLancarNfMov96] = useState<boolean>(!!etapa.gixLancarNfMov96);
  const [gixLancarNfEtapaDestino, setGixLancarNfEtapaDestino] = useState<string>(etapa.gixLancarNfEtapaDestino || '');
  const [gixLancarNfEtapaFalha, setGixLancarNfEtapaFalha] = useState<string>(etapa.gixLancarNfEtapaFalha || '');
  const [gixLancarNfEtapaServico, setGixLancarNfEtapaServico] = useState<string>((etapa as any).gixLancarNfEtapaServico || '');
  const [gixLancarNfCampoMotivo, setGixLancarNfCampoMotivo] = useState<string>(etapa.gixLancarNfCampoMotivo || '');
  // Trava mestra de escrita no GIX — é global, não é desta etapa. Mora na API GIX.
  const [nfEscritaCfg, setNfEscritaCfg] = useState<any | null>(null);
  const [nfEscritaSalvando, setNfEscritaSalvando] = useState(false);
  const [gixIntegracaoAberto, setGixIntegracaoAberto] = useState<boolean>(!!(etapa.gixBadgeCampoId || etapa.gixCriarOcCampoId || etapa.gixAprovacaoCampoId || etapa.gixLancarNfMov96));

  // Estado da trava mestra de escrita no GIX. Só busca quando a chave da etapa
  // está ligada — não faz sentido incomodar a API GIX se ninguém vai lançar nada.
  useEffect(() => {
    if (!gixLancarNfMov96) return;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(apiUrl('/gix/nf/config'));
        const j = await r.json();
        if (vivo && j?.status === 'ok') setNfEscritaCfg(j);
      } catch {
        if (vivo) setNfEscritaCfg({ allow_insert: false, indisponivel: true });
      }
    })();
    return () => { vivo = false; };
  }, [gixLancarNfMov96]);

  useEffect(() => {
    const carregarTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await fetch(`/api/documentos/templates?flowId=${flow.id}&empresaId=${flow.empresaId}`);
        if (response.ok) {
          const data = await response.json();
          setTemplatesDocumentos(data);
          // Sincronizar: se existem documentosHabilitados mas não há campos 'documento' correspondentes, inserir
          const docsHab = etapa.documentosHabilitados || [];
          if (docsHab.length > 0 && Array.isArray(data)) {
            setCampos(prev => {
              const idsExistentes = new Set(prev.map(c => c.id));
              const novos = docsHab
                .filter(tid => !idsExistentes.has(`doc_${tid}`))
                .map(tid => {
                  const tmpl = data.find((t: any) => (t._id || t.id) === tid);
                  return {
                    id: `doc_${tid}`,
                    nome: tmpl?.nome || 'Documento',
                    tipo: 'documento' as CampoConfig['tipo'],
                    obrigatorio: false,
                    documentoTemplateId: tid,
                  };
                });
              return novos.length > 0 ? [...prev, ...novos] : prev;
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    
    if (flow.id && flow.empresaId) {
      carregarTemplates();
    }
  }, [flow.id, flow.empresaId]);
  
  const [incluirOutrosDepartamentos, setIncluirOutrosDepartamentos] = useState(() => {
    if (!etapa.participantes || !etapa.departamentoId) return false;
    const colaboradoresDep = colaboradores.filter(col => 
      col.departamentoId === etapa.departamentoId && col.status === 'ativo'
    ).map(col => col.id);
    return etapa.participantes.some(participanteId => 
      !colaboradoresDep.includes(participanteId)
    );
  });
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState(() => {
    if (!etapa.participantes || !etapa.departamentoId) return '';
    const participantesOutrosDepartamentos = etapa.participantes.filter(participanteId => {
      const colaborador = colaboradores.find(col => col.id === participanteId);
      return colaborador && colaborador.departamentoId !== etapa.departamentoId;
    });
    if (participantesOutrosDepartamentos.length > 0) {
      const primeiroParticipante = colaboradores.find(col => col.id === participantesOutrosDepartamentos[0]);
      return primeiroParticipante?.departamentoId || '';
    }
    return '';
  });
  const [editandoCampo, setEditandoCampo] = useState<string | null>(null);
  const [campoEditando, setCampoEditando] = useState<CampoConfig | null>(null);
  const [novaOpcaoEdicao, setNovaOpcaoEdicao] = useState('');
  
  const [modalImportarAberto, setModalImportarAberto] = useState(false);
  const [fluxoOrigemImportar, setFluxoOrigemImportar] = useState<string>('');
  const [etapaOrigemImportar, setEtapaOrigemImportar] = useState<string>('');
  const [camposSelecionadosImportar, setCamposSelecionadosImportar] = useState<string[]>([]);
  const [todosFluxos, setTodosFluxos] = useState<Flow[]>([]);
  const [carregandoFluxos, setCarregandoFluxos] = useState(false);
  
  const [novoCampo, setNovoCampo] = useState<CampoConfig>({
    id: '',
    nome: '',
    tipo: 'texto',
    obrigatorio: false,
    opcoes: []
  });
  const [novaOpcao, setNovaOpcao] = useState('');
  const [menuCampoAberto, setMenuCampoAberto] = useState<string | null>(null);

  const podeExcluirEtapa = !cards.some(card => card.etapaId === etapa.id);

  useEffect(() => {
    if (modalImportarAberto && todosFluxos.length === 0) {
      setCarregandoFluxos(true);
      fetch(`/api/flows?empresaId=${flow.empresaId}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          let fluxos = data;
          if (data?.data && Array.isArray(data.data)) fluxos = data.data;
          if (data?.flows && Array.isArray(data.flows)) fluxos = data.flows;
          if (Array.isArray(fluxos)) {
            setTodosFluxos(fluxos);
          }
        })
        .catch(err => console.error('Erro ao carregar fluxos:', err))
        .finally(() => setCarregandoFluxos(false));
    }
  }, [modalImportarAberto, todosFluxos.length]);

  const colaboradoresDepartamento = colaboradores.filter(col => 
    col.departamentoId === departamentoId && col.status === 'ativo'
  );

  const colaboradoresOutrosDepartamentos = incluirOutrosDepartamentos && departamentoSelecionado
    ? colaboradores.filter(col => 
        col.departamentoId === departamentoSelecionado && 
        col.status === 'ativo'
      )
    : [];

  const participantesOutrosDepartamentosJaSelecionados = participantes
    .filter(pId => {
      const colab = colaboradores.find(c => c.id === pId);
      return colab && colab.departamentoId !== departamentoId;
    })
    .map(pId => {
      const colab = colaboradores.find(c => c.id === pId)!;
      const dep = departamentos.find(d => d.id === colab.departamentoId);
      return { ...colab, departamentoNome: dep?.nome || 'Desconhecido' };
    });

  const participantesPorDepartamento = participantesOutrosDepartamentosJaSelecionados.reduce((acc, colab) => {
    const depNome = colab.departamentoNome;
    if (!acc[depNome]) acc[depNome] = [];
    acc[depNome].push(colab);
    return acc;
  }, {} as Record<string, typeof participantesOutrosDepartamentosJaSelecionados>);

  const departamentosDisponiveis = departamentos.filter(dep => dep.id !== departamentoId);

  const canEditStep = userPermissions?.canAdmin || 
    checkEditorFlowPermissions(
      userPermissions?.currentUser?.role || 'viewer',
      userPermissions?.currentUser?.id || '',
      flow.createdBy || flow.responsavelId,
      'edit'
    );

  // Returns true if the user has the granular "configurarEtapas" permission for a given section and stage
  const canEditSection = (sectionId: string): boolean => {
    if (canEditStep) return true;
    const cfg = userPermissions?.configurarEtapas;
    if (!cfg?.permitido) return false;
    if (cfg.etapas?.length > 0 && !cfg.etapas.includes(etapa.id)) return false;
    if (!cfg.secoes || cfg.secoes.length === 0) return true;
    return cfg.secoes.includes(sectionId);
  };

  const canAddFields = userPermissions?.canAdmin || 
    checkEditorFlowPermissions(
      userPermissions?.currentUser?.role || 'viewer',
      userPermissions?.currentUser?.id || '',
      flow.createdBy || flow.responsavelId,
      'addField'
    );

  const adicionarOpcao = () => {
    if (!novaOpcao.trim()) return;
    let linhas: string[] = [];
    if (novaOpcao.includes(';')) {
      linhas = novaOpcao.split(';').map(l => l.trim()).filter(l => l);
    } else if (novaOpcao.includes('\t')) {
      linhas = novaOpcao.split('\t').map(l => l.trim()).filter(l => l);
    } else if (novaOpcao.includes('\n')) {
      linhas = novaOpcao.split('\n').map(l => l.trim()).filter(l => l);
    } else if (/\d+\s+\w/.test(novaOpcao)) {
      linhas = novaOpcao.split(/\d+\s+/).map(l => l.trim()).filter(l => l);
    } else {
      linhas = [novaOpcao.trim()];
    }
    if (linhas.length > 1) {
      const opcoesExistentes = novoCampo.opcoes || [];
      const novasOpcoes = linhas.filter(linha => !opcoesExistentes.includes(linha));
      if (novasOpcoes.length > 0) {
        setNovoCampo(prev => ({ ...prev, opcoes: [...opcoesExistentes, ...novasOpcoes] }));
      }
    } else {
      const opcao = novaOpcao.trim();
      if (!novoCampo.opcoes?.includes(opcao)) {
        setNovoCampo(prev => ({ ...prev, opcoes: [...(prev.opcoes || []), opcao] }));
      }
    }
    setNovaOpcao('');
  };

  const adicionarCampo = () => {
    if (novoCampo.nome.trim()) {
      const canAddField = userPermissions?.canAdmin || 
        checkEditorFlowPermissions(
          userPermissions?.currentUser?.role || 'viewer',
          userPermissions?.currentUser?.id || '',
          flow.createdBy || flow.responsavelId,
          'addField'
        );
      if (!canAddField) { alert('Você não tem permissão para adicionar campos neste fluxo.'); return; }
      const novoId = gerarIdCampo();
      setCampos(prev => [...prev, { ...novoCampo, id: novoId, createdBy: userPermissions?.currentUser?.id }]);
      setNovoCampo({ id: '', nome: '', tipo: 'texto', obrigatorio: false, opcoes: [] });
      setNovaOpcao('');
    }
  };

  const removerCampo = (campoId: string) => {
    const campo = campos.find(c => c.id === campoId);
    const canDeleteField = userPermissions?.canAdmin || 
      checkEditorFieldPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'delete', campo?.createdBy);
    if (!canDeleteField) { alert('Você não tem permissão para excluir este campo.'); return; }
    setCampos(campos.filter(campo => campo.id !== campoId));
  };

  const clonarCampo = (campoId: string) => {
    const original = campos.find(c => c.id === campoId);
    const canCloneField = userPermissions?.canAdmin || 
      checkEditorFlowPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'addField');
    if (!canCloneField) { alert('Você não tem permissão para clonar campos neste fluxo.'); return; }
    if (!original) return;
    const novoId = gerarIdCampo();
    const copia: CampoConfig = { ...original, id: novoId, nome: `${original.nome} (cópia)`, opcoes: original.opcoes ? [...original.opcoes] : undefined, createdBy: userPermissions?.currentUser?.id || original.createdBy };
    setCampos(prev => {
      const idx = prev.findIndex(c => c.id === campoId);
      if (idx === -1) return [...prev, copia];
      const novo = [...prev];
      novo.splice(idx + 1, 0, copia);
      return novo;
    });
    setMenuCampoAberto(null);
  };

  const importarCamposSelecionados = () => {
    if (!fluxoOrigemImportar || !etapaOrigemImportar || camposSelecionadosImportar.length === 0) {
      alert('Selecione pelo menos um campo para importar');
      return;
    }
    const fluxoOrigem = fluxoOrigemImportar === String(flow.id) ? flow : todosFluxos.find(f => String(f.id) === fluxoOrigemImportar);
    if (!fluxoOrigem) { alert('Fluxo de origem não encontrado'); return; }
    const etapaOrigem = fluxoOrigem.etapas?.find(e => String(e.id) === etapaOrigemImportar);
    if (!etapaOrigem) { alert('Etapa de origem não encontrada'); return; }
    const camposImportados: CampoConfig[] = [];
    camposSelecionadosImportar.forEach(campoId => {
      const campoOriginal = etapaOrigem.campos?.find(c => c.id === campoId);
      if (campoOriginal) {
        const novoId = gerarIdCampo();
        camposImportados.push({ ...campoOriginal, id: novoId, opcoes: campoOriginal.opcoes ? [...campoOriginal.opcoes] : undefined, createdBy: userPermissions?.currentUser?.id || campoOriginal.createdBy });
      }
    });
    setCampos(prev => [...prev, ...camposImportados]);
    setModalImportarAberto(false);
    setFluxoOrigemImportar('');
    setEtapaOrigemImportar('');
    setCamposSelecionadosImportar([]);
    alert(`${camposImportados.length} campo(s) importado(s) com sucesso!`);
  };

  const getEtapasParaImportar = () => {
    if (!fluxoOrigemImportar) return [];
    if (fluxoOrigemImportar === String(flow.id)) {
      return (flow.etapas || []).filter(e => String(e.id) !== String(etapa.id) && e.campos && e.campos.length > 0);
    }
    const fluxoSelecionado = todosFluxos.find(f => String(f.id) === fluxoOrigemImportar);
    if (!fluxoSelecionado) return [];
    return (fluxoSelecionado.etapas || []).filter(e => e.campos && e.campos.length > 0);
  };
  const etapasParaImportar = getEtapasParaImportar();

  const iniciarEdicaoCampo = (campo: CampoConfig) => {
    const canEditField = userPermissions?.canAdmin || 
      checkEditorFieldPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'edit', campo.createdBy);
    if (!canEditField) { alert('Você não tem permissão para editar este campo.'); return; }
    setEditandoCampo(campo.id);
    setCampoEditando({ ...campo });
    setNovaOpcaoEdicao('');
  };

  const cancelarEdicaoCampo = () => { setEditandoCampo(null); setCampoEditando(null); setNovaOpcaoEdicao(''); };

  const salvarEdicaoCampo = () => {
    if (!campoEditando || !campoEditando.nome.trim()) { alert('Nome do campo é obrigatório'); return; }
    setCampos(campos.map(c => c.id === campoEditando.id ? { ...campoEditando } : c));
    setEditandoCampo(null);
    setCampoEditando(null);
    setNovaOpcaoEdicao('');
  };

  const adicionarOpcaoEdicao = () => {
    if (!novaOpcaoEdicao.trim() || !campoEditando) return;
    let linhas: string[] = [];
    if (novaOpcaoEdicao.includes(';')) { linhas = novaOpcaoEdicao.split(';').map(l => l.trim()).filter(l => l); }
    else if (novaOpcaoEdicao.includes('\t')) { linhas = novaOpcaoEdicao.split('\t').map(l => l.trim()).filter(l => l); }
    else if (novaOpcaoEdicao.includes('\n')) { linhas = novaOpcaoEdicao.split('\n').map(l => l.trim()).filter(l => l); }
    else if (/\d+\s+\w/.test(novaOpcaoEdicao)) { linhas = novaOpcaoEdicao.split(/\d+\s+/).map(l => l.trim()).filter(l => l); }
    else { linhas = [novaOpcaoEdicao.trim()]; }
    if (linhas.length > 1) {
      const opcoesExistentes = campoEditando.opcoes || [];
      const novasOpcoes = linhas.filter(linha => !opcoesExistentes.includes(linha));
      if (novasOpcoes.length > 0) { setCampoEditando(prev => prev ? ({ ...prev, opcoes: [...opcoesExistentes, ...novasOpcoes] }) : null); }
    } else {
      const opcao = novaOpcaoEdicao.trim();
      if (!campoEditando.opcoes?.includes(opcao)) { setCampoEditando(prev => prev ? ({ ...prev, opcoes: [...(prev.opcoes || []), opcao] }) : null); }
    }
    setNovaOpcaoEdicao('');
  };

  const handleToggleParticipante = (colaboradorId: string) => {
    setParticipantes(prev => prev.includes(colaboradorId) ? prev.filter(id => id !== colaboradorId) : [...prev, colaboradorId]);
  };

  const converterTipoCampo = (campoId: string, novoTipo: CampoConfig['tipo']) => {
    const campo = campos.find(c => c.id === campoId);
    if (!campo) return;
    const canEditField = userPermissions?.canAdmin || 
      checkEditorFieldPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'edit', campo.createdBy);
    if (!canEditField) { alert('Você não tem permissão para editar este campo.'); return; }
    const tiposComOpcoes = ['lista', 'checkbox', 'checkbox_single', 'checkbox_multi', 'progresso'];
    const campoOriginalTemOpcoes = tiposComOpcoes.includes(campo.tipo);
    const novoTipoTemOpcoes = tiposComOpcoes.includes(novoTipo);
    setCampos(campos.map(c => {
      if (c.id !== campoId) return c;
      return { ...c, tipo: novoTipo, opcoes: (campoOriginalTemOpcoes && novoTipoTemOpcoes) ? c.opcoes : undefined };
    }));
    setMenuCampoAberto(null);
  };

  const handleToggleOutrosDepartamentos = (incluir: boolean) => {
    setIncluirOutrosDepartamentos(incluir);
    if (!incluir) {
      setDepartamentoSelecionado('');
      const idsColaboradoresDepartamentoPrincipal = colaboradoresDepartamento.map(col => col.id);
      setParticipantes(prev => prev.filter(id => idsColaboradoresDepartamentoPrincipal.includes(id)));
    }
  };

  const handleChangeDepartamentoSelecionado = (novoDepartamento: string) => {
    setDepartamentoSelecionado(novoDepartamento);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEndCampos = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = campos.findIndex(c => c.id === String(active.id));
    const newIndex = campos.findIndex(c => c.id === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setCampos(arrayMove(campos, oldIndex, newIndex));
  };

  const hasAnyEditPermission = canEditStep || (() => {
    const cfg = userPermissions?.configurarEtapas;
    if (!cfg?.permitido) return false;
    if (cfg.etapas?.length > 0 && !cfg.etapas.includes(etapa.id)) return false;
    return true;
  })();

  const handleSave = () => {
    if (!hasAnyEditPermission) { alert('Você não tem permissão para editar esta etapa.'); return; }
    // ⚠️ Commitar rascunho de modelo aberto (caso o usuário clique em "Salvar" do rodapé
    // sem ter clicado no "Aplicar" interno do modelo). Sem isso, alterações como
    // recorrência ficam só no draft e são descartadas ao salvar a etapa.
    const cardsPersonalizadosFinal = (templateDraft && templateEditandoId)
      ? cardsPersonalizados.map(t => t.id === templateDraft.id ? { ...templateDraft } : t)
      : cardsPersonalizados;
    if (templateDraft && templateEditandoId) {
      setCardsPersonalizados(cardsPersonalizadosFinal);
      setTemplateEditandoId(null);
      setTemplateDraft(null);
    }
    // Derivar documentosHabilitados a partir dos campos tipo documento (manter sincronizado com a ordem)
    const docsHabFromCampos = campos
      .filter(c => c.tipo === 'documento')
      .map(c => c.documentoTemplateId || c.id.replace('doc_', ''));
    const etapaAtualizada: Etapa = {
      ...etapa,
      nome: nomeEtapa,
      campos,
      departamentoId,
      participantes,
      permiteMoverPara,
      permiteCriarCards,
      solicitarReenvioAnexos: solicitarReenvioAnexos || undefined,
      etapaRetornoReenvioId: solicitarReenvioAnexos ? (etapaRetornoReenvioId || undefined) : undefined,
      etiquetaReenvioNome: solicitarReenvioAnexos ? (etiquetaReenvioNome.trim() || undefined) : undefined,
      etiquetaReenvioCor: solicitarReenvioAnexos && etiquetaReenvioNome.trim() ? etiquetaReenvioCor : undefined,
      responsavelPadraoId: responsavelPadraoId || undefined,
      responsavelQuemMoveu: responsavelQuemMoveu || undefined,
      regrasResponsavel: regrasResponsavelTipo && regrasResponsavelCondicoes.length > 0 ? {
        tipo: regrasResponsavelTipo,
        condicoes: regrasResponsavelCondicoes
      } : undefined,
      tempoMaximoHoras: tempoMaximoHoras > 0 ? tempoMaximoHoras : undefined,
      slaIgnorarFimDeSemana: tempoMaximoHoras > 0 && slaIgnorarFimDeSemana ? true : undefined,
      mostrarCampoTitulo,
      limparCamposAoRetornar: limparCamposAtivo && camposParaLimparAoRetornar.length > 0 ? {
        ativo: true,
        campos: camposParaLimparAoRetornar
      } : undefined,
      documentosHabilitados: docsHabFromCampos,
      camposVisiveisNoCard: camposVisiveisTmp,
      camposTravados: camposTravadosTmp,
      camposOutrasEtapas: camposOutrasEtapas,
      notificacoesEmail: {
        habilitado: notificacoesEmailHabilitado,
        colaboradores: notificacoesEmailColaboradores
      },
      notificacoesWhatsapp: {
        habilitado: notificacoesWhatsappHabilitado,
        colaboradores: notificacoesWhatsappColaboradores
      },
      camposWhatsapp: camposWhatsappHabilitado ? {
        habilitado: true,
        campos: camposWhatsappSelecionados,
        colaboradores: camposWhatsappColaboradores
      } : undefined,
      alertaSlaWhatsapp: alertaSlaWhatsappHabilitado && tempoMaximoHoras > 0 ? {
        habilitado: true
      } : undefined,
      escalonamentoSla: escalonamentoSlaHabilitado && tempoMaximoHoras > 0 ? {
        habilitado: true,
        destinatariosExtras: escalonamentoDestinatarios,
        emailsExtras: escalonamentoEmails,
      } : undefined,
      cardsPersonalizados: cardsPersonalizadosFinal.length > 0 ? cardsPersonalizadosFinal : undefined,
      // 💰 O que a cotação leva por padrão. Vazio dos dois lados = sem modelo.
      cotacaoCampos: (cotacaoContexto.length || cotacaoResposta.length)
        ? { contexto: cotacaoContexto, resposta: cotacaoResposta }
        : undefined,
      cotacaoFornecedores: (() => {
        const limpos = cotacaoFornecedores
          .map(f => ({
            nome: (f.nome || '').trim(),
            email: (f.email || '').trim(),
            telefone: (f.telefone || '').trim(),
          }))
          .filter(f => f.nome);
        return limpos.length ? limpos : undefined;
      })(),
      gixBadgeCampoId: gixBadgeCampoId || undefined,
      gixCriarOcCampoId: gixCriarOcCampoId || undefined,
      gixAprovacaoCampoId: gixAprovacaoCampoId || undefined,
      gixAutoAprovarMov96: gixAutoAprovarMov96 || undefined,
      gixLancarNfMov96: gixLancarNfMov96 || undefined,
      gixLancarNfEtapaDestino: gixLancarNfEtapaDestino || undefined,
      gixLancarNfEtapaFalha: gixLancarNfEtapaFalha || undefined,
      gixLancarNfEtapaServico: gixLancarNfEtapaServico || undefined,
      gixLancarNfCampoMotivo: gixLancarNfCampoMotivo || undefined,
      // Só o rótulo e os campos são configuráveis. O resto deixou de ser opção e
      // vai fixo — inclusive limpando o que ficou salvo de configurações antigas,
      // que hoje ninguém mais consegue ver nem corrigir pela tela.
      configuracaoFatiamento: fatiarHabilitado ? {
        habilitado: true,
        rotulo: fatiarRotulo.trim() || undefined,
        camposFatiaveis: fatiarCamposFatiaveis.length > 0 ? fatiarCamposFatiaveis : undefined,
        camposSempreJuntos: undefined,
        etapaDestinoId: undefined,   // a fatia sempre nasce nesta mesma etapa
        prefixoTitulo: undefined,    // "[FATIA {n}] {titulo}", editável na hora de fatiar
        copiarAnexos: true,
        origemMantemCampos: false    // os campos marcados saem da origem
      } : undefined
    };
    onSave(etapaAtualizada);
  };

  // Definicao das abas
  const abaIcons: Record<string, React.ReactNode> = {
    geral: <Cog6ToothIcon className="w-5 h-5" />,
    participantes: <UserGroupIcon className="w-5 h-5" />,
    campos: <DocumentTextIcon className="w-5 h-5" />,
    fluxo: <ArrowPathIcon className="w-5 h-5" />,
    notificacoes: <BellIcon className="w-5 h-5" />,
    templates: <Squares2X2Icon className="w-5 h-5" />,
  };
  const abas = [
    { id: 'geral' as const, label: 'Geral', desc: 'Nome, departamento e regras' },
    { id: 'participantes' as const, label: 'Equipe', desc: 'Participantes da etapa' },
    { id: 'campos' as const, label: 'Campos', desc: 'Campos e formularios' },
    { id: 'fluxo' as const, label: 'Fluxo', desc: 'Criacao e movimentacao' },
    { id: 'notificacoes' as const, label: 'Alertas', desc: 'E-mail e WhatsApp' },
    { id: 'templates' as const, label: 'Cards Personalizados', desc: 'Modelos com campos pré-preenchidos' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 md:p-2 z-50">
      <div className="relative overflow-hidden bg-white md:rounded-2xl shadow-2xl w-full max-w-6xl h-full md:h-auto md:max-h-[97vh] flex flex-col">
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500" />

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Configurar Etapa</h2>
              <p className="text-xs text-gray-500">{nomeEtapa || 'Sem nome'} &bull; {departamentos.find(d => d.id === departamentoId)?.nome || 'Sem departamento'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1.5 rounded-lg transition-all">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* MOBILE TAB BAR */}
        <div className="md:hidden border-b border-gray-100 bg-gray-50/80 overflow-x-auto">
          <div className="flex">
            {abas.map(aba => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-all ${
                  abaAtiva === aba.id
                    ? 'border-indigo-600 text-indigo-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="w-4 h-4">{abaIcons[aba.id]}</span>
                {aba.label}
              </button>
            ))}
          </div>
        </div>

        {/* BODY: SIDEBAR + CONTENT */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - hidden on mobile */}
          <nav className="hidden md:block w-44 flex-shrink-0 bg-gray-50 border-r border-gray-100 py-3 overflow-y-auto">
            <div className="space-y-0.5 px-2">
              {abas.map(aba => (
                <button
                  key={aba.id}
                  onClick={() => setAbaAtiva(aba.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all group ${
                    abaAtiva === aba.id
                      ? 'bg-white shadow-sm border border-gray-200 text-gray-900'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`flex-shrink-0 ${abaAtiva === aba.id ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`}>{abaIcons[aba.id]}</span>
                    <div>
                      <div className={`text-sm font-medium ${abaAtiva === aba.id ? 'text-gray-900' : ''}`}>{aba.label}</div>
                      <div className="text-[10px] text-gray-400 leading-tight">{aba.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {/* Quick stats */}
            <div className="mt-4 px-3 pt-3 border-t border-gray-200 space-y-2">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-gray-500">Participantes</span>
                <span className={`font-semibold ${participantes.length > 0 ? 'text-green-600' : 'text-amber-500'}`}>{participantes.length}</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-gray-500">Campos</span>
                <span className="font-semibold text-purple-600">{campos.length}</span>
              </div>
              {tempoMaximoHoras > 0 && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-gray-500">SLA</span>
                  <span className="font-semibold text-amber-600">{tempoMaximoHoras}h</span>
                </div>
              )}
            </div>
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-3 md:p-5">

            {/* ============ ABA GERAL ============ */}
            {abaAtiva === 'geral' && (
              <div className="space-y-5">
                {/* Informacoes Basicas */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-sm font-semibold text-gray-800">Informações Básicas</h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Nome da Etapa</label>
                      <input value={nomeEtapa} onChange={(e) => setNomeEtapa(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" placeholder="Nome da etapa" disabled={!canEditStep} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Departamento Responsável</label>
                      <select value={departamentoId} onChange={(e) => { setDepartamentoId(e.target.value); setParticipantes([]); }} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" disabled={!canEditStep}>
                        <option value="">Selecione um departamento</option>
                        {departamentos.map(dep => (<option key={dep.id} value={dep.id}>{dep.nome}</option>))}
                      </select>
                    </div>
                    {!canEditStep && (
                      <p className="text-xs text-gray-400 flex items-center gap-1"><LockClosedIcon className="h-3 w-3" /> Sem permissão para editar esta etapa</p>
                    )}
                  </div>
                </section>

                {/* Responsável Padrão */}
                {participantes.length > 0 && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                      <h3 className="text-sm font-semibold text-gray-800">Responsável Padrão</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Colaborador atribuído automaticamente aos novos cards</p>
                    </div>
                    <div className="p-5 space-y-3">
                      <select value={responsavelPadraoId} onChange={(e) => setResponsavelPadraoId(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" disabled={!canEditStep}>
                        <option value="">Nenhum responsável padrão</option>
                        {participantes.map(pId => { const col = colaboradores.find(c => c.id === pId); return col ? <option key={pId} value={pId}>{col.nome} {col.cargo ? `- ${col.cargo}` : ''}</option> : null; })}
                      </select>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={responsavelQuemMoveu}
                          onChange={(e) => setResponsavelQuemMoveu(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          disabled={!canEditStep}
                        />
                        <span>
                          <span className="block text-xs font-medium text-gray-700">Quem mover o card para cá vira o responsável</span>
                          <span className="block text-[10px] text-gray-500 mt-0.5">
                            Vale ao arrastar ou usar "Mover para". Passa na frente das regras condicionais e do responsável padrão acima.
                            Se o card chegar por automação, as regras normais continuam valendo.
                          </span>
                        </span>
                      </label>
                    </div>
                  </section>
                )}

                {/* Regras Condicionais de Responsável */}
                {participantes.length > 0 && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <button onClick={() => setRegrasCondicionaisAberto(!regrasCondicionaisAberto)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 text-left">Regras Condicionais de Responsável</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">Atribuir responsáveis baseado em condições</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {regrasResponsavelTipo && (
                          <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                            {regrasResponsavelTipo === 'criador' ? 'Criador' : 'Resp. Anterior'}
                          </span>
                        )}
                        <span className={`text-gray-400 transition-transform ${regrasCondicionaisAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                      </div>
                    </button>
                    {regrasCondicionaisAberto && (
                      <div className="px-5 pb-5 space-y-3 border-t border-gray-100 pt-4">
                        <select value={regrasResponsavelTipo} onChange={(e) => { const novoTipo = e.target.value as typeof regrasResponsavelTipo; setRegrasResponsavelTipo(novoTipo); setRegrasResponsavelCondicoes(novoTipo ? [{ quando: '', atribuirPara: '' }] : []); }} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" disabled={!canEditStep}>
                          <option value="">Nenhuma regra condicional</option>
                          <option value="criador">Baseado em quem criou/preencheu o card</option>
                          <option value="responsavel_anterior">Baseado no responsável da etapa anterior</option>
                        </select>
                        {regrasResponsavelTipo && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700">Condições:</span>
                              <button onClick={() => setRegrasResponsavelCondicoes([...regrasResponsavelCondicoes, { quando: '', atribuirPara: '' }])} className="text-[10px] px-2 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700" disabled={!canEditStep}>+ Condição</button>
                            </div>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {regrasResponsavelCondicoes.map((condicao, index) => (
                                <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">{regrasResponsavelTipo === 'criador' ? 'Criado por' : 'Resp. anterior'}</label>
                                    <select value={condicao.quando} onChange={(e) => { const n = [...regrasResponsavelCondicoes]; n[index].quando = e.target.value; setRegrasResponsavelCondicoes(n); }} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md" disabled={!canEditStep}>
                                      <option value="">Selecione...</option>
                                      {regrasResponsavelTipo === 'criador' && <option value="card_creator">Qualquer criador</option>}
                                      {colaboradores.map(col => <option key={col.id} value={col.userId || col.id}>{col.nome}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">Atribuir para</label>
                                    <select value={condicao.atribuirPara} onChange={(e) => { const n = [...regrasResponsavelCondicoes]; n[index].atribuirPara = e.target.value; setRegrasResponsavelCondicoes(n); }} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md" disabled={!canEditStep}>
                                      <option value="">Selecione...</option>
                                      {participantes.map(pId => { const col = colaboradores.find(c => c.id === pId); return col ? <option key={pId} value={pId}>{col.nome}</option> : null; })}
                                    </select>
                                  </div>
                                  <button onClick={() => setRegrasResponsavelCondicoes(regrasResponsavelCondicoes.filter((_, i) => i !== index))} className="self-end text-red-400 hover:text-red-600 p-1.5" disabled={!canEditStep}>
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {regrasResponsavelCondicoes.length === 0 && (
                              <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-lg">Clique em "+ Condição" para criar regras</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}

                {/* SLA */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setSlaAberto(!slaAberto)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 text-left">Tempo Máximo (SLA)</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Tempo máximo que um card pode ficar nesta etapa</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {tempoMaximoHoras > 0 && <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">{tempoMaximoHoras}h</span>}
                      <span className={`text-gray-400 transition-transform ${slaAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                    </div>
                  </button>
                  {slaAberto && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                      <div className="flex items-center gap-3">
                        <input type="number" min="0" step="0.5" value={tempoMaximoHoras || ''} onChange={(e) => setTempoMaximoHoras(Number(e.target.value))} placeholder="0" className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" disabled={!canEditStep} />
                        <span className="text-sm text-gray-600">horas</span>
                      </div>
                      {tempoMaximoHoras > 0 && (
                        <>
                          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            Cards que ficarem mais de <strong>{tempoMaximoHoras}h</strong> nesta etapa serão marcados como atrasados.
                            ({tempoMaximoHoras < 8 ? `${(tempoMaximoHoras * 60).toFixed(0)} minutos` : `${(tempoMaximoHoras / 24).toFixed(1)} dias`})
                          </p>
                          <div className="mt-3 flex items-center gap-3">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input type="checkbox" checked={slaIgnorarFimDeSemana} onChange={(e) => setSlaIgnorarFimDeSemana(e.target.checked)} className="sr-only peer" disabled={!canEditStep} />
                              <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
                            </label>
                            <span className="text-sm text-gray-700">Ignorar fins de semana</span>
                          </div>
                          {slaIgnorarFimDeSemana && (
                            <p className="mt-1 text-[10px] text-gray-500">Sábados e domingos não serão contados no tempo de SLA.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </section>

                {/* Limpar Campos ao Retornar */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setLimparCamposAberto(!limparCamposAberto)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 text-left">Limpar Campos ao Retornar</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Limpar valores quando card voltar para esta etapa</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {limparCamposAtivo && camposParaLimparAoRetornar.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full font-medium">{camposParaLimparAoRetornar.length} campo{camposParaLimparAoRetornar.length > 1 ? 's' : ''}</span>
                      )}
                      <span className={`text-gray-400 transition-transform ${limparCamposAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                    </div>
                  </button>
                  {limparCamposAberto && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-3">
                      <p className="text-xs text-gray-600">Quando um card <strong>retornar</strong> para esta etapa, os campos selecionados serão limpos. Histórico preservado.</p>
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={limparCamposAtivo} onChange={(e) => { setLimparCamposAtivo(e.target.checked); if (!e.target.checked) setCamposParaLimparAoRetornar([]); }} className="sr-only peer" disabled={!canEditStep} />
                          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
                        </label>
                        <span className="text-sm text-gray-700">{limparCamposAtivo ? 'Ativado' : 'Desativado'}</span>
                      </div>
                      {limparCamposAtivo && (
                        <div className="space-y-2">
                          <div className="flex gap-2 mb-2">
                            <button type="button" onClick={() => setCamposParaLimparAoRetornar(campos.map(c => c.id))} className="text-[10px] px-2 py-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border border-cyan-200 rounded" disabled={!canEditStep}>Selecionar todos</button>
                            <button type="button" onClick={() => setCamposParaLimparAoRetornar([])} className="text-[10px] px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 rounded" disabled={!canEditStep}>Limpar seleção</button>
                          </div>
                          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                            {campos.map((campo) => (
                              <label key={campo.id} className="flex items-center gap-3 px-3 py-2 hover:bg-cyan-50 cursor-pointer transition-colors">
                                <input type="checkbox" checked={camposParaLimparAoRetornar.includes(campo.id)} onChange={(e) => { e.target.checked ? setCamposParaLimparAoRetornar([...camposParaLimparAoRetornar, campo.id]) : setCamposParaLimparAoRetornar(camposParaLimparAoRetornar.filter(id => id !== campo.id)); }} className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500" disabled={!canEditStep} />
                                <span className="text-sm text-gray-800">{campo.nome}</span>
                                <span className="text-[10px] text-gray-400">({campo.tipo})</span>
                              </label>
                            ))}
                          </div>
                          {campos.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Nenhum campo configurado nesta etapa.</p>}
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {/* Integração GIX (somente ambiente local) */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setGixIntegracaoAberto(!gixIntegracaoAberto)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 text-left">Integração GIX (local)</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Configurar badge de status da OC e botão "Criar OC" nos campos desta etapa</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {(gixBadgeCampoId || gixCriarOcCampoId || gixAprovacaoCampoId || gixAutoAprovarMov96 || gixLancarNfMov96) && (
                        <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                          ativo
                        </span>
                      )}
                      <span className={`text-gray-400 transition-transform ${gixIntegracaoAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                    </div>
                  </button>
                  {gixIntegracaoAberto && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                      <p className="text-xs text-gray-600">
                        A integração GIX só funciona em <strong>ambiente local</strong> (não funciona na VPS). Escolha em qual campo renderizar o badge/botão; deixe em branco para desabilitar.
                      </p>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Campo para exibir <span className="text-emerald-700">badge de status da OC</span>
                        </label>
                        <select
                          value={gixBadgeCampoId}
                          onChange={(e) => setGixBadgeCampoId(e.target.value)}
                          disabled={!canEditStep}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                        >
                          <option value="">— desabilitado —</option>
                          {campos.map((c) => (
                            <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                          ))}
                          {camposOutrasEtapas.map((campoKey) => {
                            const [outraEtapaId, outroCampoId] = campoKey.split(':');
                            const outraEtapa = flow?.etapas?.find(e => String(e.id) === outraEtapaId);
                            const outroCampo = outraEtapa?.campos?.find(c => c.id === outroCampoId);
                            if (!outraEtapa || !outroCampo) return null;
                            return (
                              <option key={campoKey} value={campoKey}>
                                📌 {outraEtapa.nome} → {outroCampo.nome} ({outroCampo.tipo})
                              </option>
                            );
                          })}
                        </select>
                        <p className="mt-1 text-[10px] text-gray-500">
                          Quando o card for aberto, o valor deste campo será usado para consultar <code className="px-1 bg-gray-100 rounded">/api/gix/oc/&#123;valor&#125;</code> e o badge aparecerá abaixo do campo. Clicar no badge abre o modal de detalhes.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Campo para exibir <span className="text-emerald-700">botão "Criar OC no GIX"</span>
                        </label>
                        <select
                          value={gixCriarOcCampoId}
                          onChange={(e) => setGixCriarOcCampoId(e.target.value)}
                          disabled={!canEditStep}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                        >
                          <option value="">— desabilitado —</option>
                          {campos.map((c) => (
                            <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                          ))}
                        </select>
                        <p className="mt-1 text-[10px] text-gray-500">
                          O botão aparece acima deste campo quando ele está vazio. Ao criar a OC, o número retornado é gravado automaticamente neste mesmo campo.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Campo de <span className="text-emerald-700">aprovação da OC via WhatsApp</span>
                        </label>
                        <select
                          value={gixAprovacaoCampoId}
                          onChange={(e) => setGixAprovacaoCampoId(e.target.value)}
                          disabled={!canEditStep}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                        >
                          <option value="">— desabilitado —</option>
                          {campos.filter(c => c.tipo === 'lista' || c.tipo === 'texto').map((c) => (
                            <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                          ))}
                        </select>
                        <p className="mt-1 text-[10px] text-gray-500">
                          Quando este campo for respondido com <strong>"Sim"</strong> via WhatsApp, a OC do campo de badge é liberada automaticamente no GIX (mesma ação do botão 🔓 Liberar OC). Funciona apenas se o campo de badge estiver configurado acima.
                        </p>
                      </div>

                      <div className="pt-1">
                        <label className={`flex items-start gap-2.5 ${canEditStep ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
                          <input
                            type="checkbox"
                            checked={gixAutoAprovarMov96}
                            onChange={(e) => setGixAutoAprovarMov96(e.target.checked)}
                            disabled={!canEditStep}
                            className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                          <span>
                            <span className="block text-xs font-semibold text-gray-700">
                              Aprovar automaticamente OCs com <span className="text-emerald-700">movimento 96</span> (E96)
                            </span>
                            <span className="block mt-0.5 text-[10px] text-gray-500">
                              Esta etapa varre os cards, lê o nº da OC do campo de badge configurado acima e, se a OC tiver tipo de movimento 96 no GIX, aprova automaticamente. A aprovação real só ocorre com <code className="px-1 bg-gray-100 rounded">GIX_OC_AUTO_APROVAR=1</code> na API GIX (senão roda em modo simulação/dry-run e apenas mostra o badge).
                            </span>
                          </span>
                        </label>
                      </div>

                      {/* ---- Lançamento automático da nota fiscal (E96) ---- */}
                      <div className="pt-3 mt-3 border-t border-gray-200">
                        <label className={`flex items-start gap-2.5 ${canEditStep ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
                          <input
                            type="checkbox"
                            checked={gixLancarNfMov96}
                            onChange={(e) => setGixLancarNfMov96(e.target.checked)}
                            disabled={!canEditStep}
                            className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                          <span>
                            <span className="block text-xs font-semibold text-gray-700">
                              Lançar a <span className="text-emerald-700">nota fiscal</span> no GIX automaticamente (só E96)
                            </span>
                            <span className="block mt-0.5 text-[10px] text-gray-500">
                              Ao cair nesta etapa, o sistema procura a NF no acervo de XML que o GIX já baixou da SEFAZ, confere contra a OC e lança a entrada. Só trata <strong>uso e consumo (E96)</strong> — mercadoria com estoque é recusada. O lançamento real exige <code className="px-1 bg-gray-100 rounded">GIX_NF_ALLOW_INSERT=1</code> na API GIX; sem isso roda em dry-run e o card não sai do lugar.
                            </span>
                          </span>
                        </label>

                        {gixLancarNfMov96 && (
                          <div className="mt-3 ml-6 space-y-3">
                            {/* ---- Trava mestra: vale para o sistema inteiro ---- */}
                            <div className={`rounded-lg border p-3 ${
                              nfEscritaCfg?.allow_insert
                                ? 'border-red-300 bg-red-50'
                                : 'border-amber-300 bg-amber-50'}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-gray-800">
                                    {nfEscritaCfg?.allow_insert
                                      ? '🔴 Escrita no GIX LIGADA'
                                      : '🧪 Modo simulação (dry-run)'}
                                  </p>
                                  <p className="mt-0.5 text-[10px] text-gray-600">
                                    {nfEscritaCfg?.allow_insert
                                      ? 'As notas são lançadas de verdade no ERP. Desfazer exige estorno, não dá para apagar.'
                                      : 'O sistema confere tudo e mostra o que faria, mas não escreve nada no GIX.'}
                                  </p>
                                  {nfEscritaCfg?.alteradoPor && (
                                    <p className="mt-1 text-[10px] text-gray-500">
                                      Última mudança: {nfEscritaCfg.alteradoPor} em {nfEscritaCfg.alteradoEm}
                                    </p>
                                  )}
                                  <p className="mt-1 text-[10px] text-gray-500">
                                    Esta chave é <strong>global</strong> — vale para todas as etapas e fluxos, não só esta.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  disabled={nfEscritaSalvando || !userPermissions?.canAdmin || nfEscritaCfg?.travadoPorAmbiente}
                                  onClick={async () => {
                                    const ligando = !nfEscritaCfg?.allow_insert;
                                    const aviso = ligando
                                      ? 'LIGAR a escrita no GIX?\n\nA partir de agora o botão de lançar vai criar entrada de nota fiscal de verdade no ERP, gerar título a pagar e baixar a ordem de compra.\n\nDesfazer exige estorno pela contabilidade.'
                                      : 'Desligar a escrita e voltar para simulação?';
                                    if (!window.confirm(aviso)) return;
                                    setNfEscritaSalvando(true);
                                    try {
                                      const r = await fetch(apiUrl('/gix/nf/config'), {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          allow_insert: ligando,
                                          usuario: userPermissions?.currentUser?.nome
                                            || userPermissions?.currentUser?.email || 'Escalone',
                                        }),
                                      });
                                      const j = await r.json();
                                      if (j?.status === 'ok') setNfEscritaCfg(j);
                                      else window.alert(j?.message || 'Não consegui mudar a trava.');
                                    } catch (err) {
                                      window.alert('Erro ao falar com a API GIX: ' + err);
                                    } finally {
                                      setNfEscritaSalvando(false);
                                    }
                                  }}
                                  className={`flex-shrink-0 px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                                    nfEscritaSalvando || !userPermissions?.canAdmin || nfEscritaCfg?.travadoPorAmbiente
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : nfEscritaCfg?.allow_insert
                                        ? 'bg-gray-700 text-white hover:bg-gray-800'
                                        : 'bg-red-600 text-white hover:bg-red-700'}`}
                                >
                                  {nfEscritaSalvando ? '...' : nfEscritaCfg?.allow_insert ? 'Voltar p/ simulação' : 'Ligar escrita'}
                                </button>
                              </div>
                              {nfEscritaCfg?.travadoPorAmbiente && (
                                <p className="mt-2 text-[10px] text-gray-600">
                                  Travada pelo servidor (<code className="px-1 bg-white rounded">GIX_NF_ALLOW_INSERT</code> no systemd) — só muda por lá.
                                </p>
                              )}
                              {!userPermissions?.canAdmin && (
                                <p className="mt-2 text-[10px] text-gray-600">Só administradores podem mexer nesta chave.</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                Etapa quando lançar com sucesso
                              </label>
                              <select
                                value={gixLancarNfEtapaDestino}
                                onChange={(e) => setGixLancarNfEtapaDestino(e.target.value)}
                                disabled={!canEditStep}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">— a etapa seguinte do fluxo —</option>
                                {(flow.etapas || []).filter(e => String(e.id) !== String(etapa.id)).map(e => (
                                  <option key={e.id} value={String(e.id)}>{e.nome}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                Etapa quando <span className="text-red-600">não</span> lançar
                              </label>
                              <select
                                value={gixLancarNfEtapaFalha}
                                onChange={(e) => setGixLancarNfEtapaFalha(e.target.value)}
                                disabled={!canEditStep}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">— não mover, deixar parado aqui —</option>
                                {(flow.etapas || []).filter(e => String(e.id) !== String(etapa.id)).map(e => (
                                  <option key={e.id} value={String(e.id)}>{e.nome}</option>
                                ))}
                              </select>
                              <p className="mt-1 text-[10px] text-gray-500">
                                Crie uma etapa <strong>"Notas não lançadas"</strong> e aponte aqui. O card cai lá com o motivo escrito, em vez de ficar travado sem explicação.
                              </p>
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                Etapa para <span className="text-blue-600">nota de serviço</span>
                              </label>
                              <select
                                value={gixLancarNfEtapaServico}
                                onChange={(e) => setGixLancarNfEtapaServico(e.target.value)}
                                disabled={!canEditStep}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">— não mover, deixar com a contabilidade —</option>
                                {(flow.etapas || []).filter(e => String(e.id) !== String(etapa.id)).map(e => (
                                  <option key={e.id} value={String(e.id)}>{e.nome}</option>
                                ))}
                              </select>
                              <p className="mt-1 text-[10px] text-gray-500">
                                Nota de serviço não tem XML na SEFAZ nem ordem de compra, então é <strong>sempre</strong> lançada à mão: o contábil abre o anexo do card e lança pela tela do GIX. Ela <strong>não</strong> vai para "Notas não lançadas" — não é erro, é rotina, e misturar as duas esconde os problemas de verdade.
                              </p>
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                Campo que recebe o motivo
                              </label>
                              <select
                                value={gixLancarNfCampoMotivo}
                                onChange={(e) => setGixLancarNfCampoMotivo(e.target.value)}
                                disabled={!canEditStep}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">— só no histórico do card —</option>
                                {campos.filter(c => c.tipo === 'texto' || c.tipo === 'texto_longo').map(c => (
                                  <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                                ))}
                              </select>
                              <p className="mt-1 text-[10px] text-gray-500">
                                Ex.: "Divergências encontradas". O motivo é sempre gravado no histórico do card, mesmo sem campo escolhido.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* ============ ABA PARTICIPANTES ============ */}
            {abaAtiva === 'participantes' && (
              <div className="space-y-5">
                {!departamentoId ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                    <p className="text-amber-800 text-sm font-medium">Selecione um departamento na aba "Geral" primeiro.</p>
                  </div>
                ) : (
                  <>
                    {participantes.length > 0 && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
                        <h4 className="text-sm font-semibold text-green-800 mb-2">{participantes.length} participante{participantes.length > 1 ? 's' : ''} selecionado{participantes.length > 1 ? 's' : ''}</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {participantes.map(pId => {
                            const col = colaboradores.find(c => c.id === pId);
                            const dep = departamentos.find(d => d.id === col?.departamentoId);
                            const isOutro = dep && dep.id !== departamentoId;
                            return (
                              <span key={pId} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${isOutro ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-green-100 text-green-800'}`}>{col?.nome}{isOutro && <span className="text-[9px] text-blue-500">({dep?.nome})</span>}</span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">{departamentos.find(d => d.id === departamentoId)?.nome || 'Departamento Principal'}</h3>
                        {!incluirOutrosDepartamentos && canEditStep && (
                          <button onClick={() => setIncluirOutrosDepartamentos(true)} className="text-[10px] px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md transition-colors">+ Outros departamentos</button>
                        )}
                      </div>
                      <div className="p-4">
                        {colaboradoresDepartamento.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-3">Nenhum colaborador ativo neste departamento</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {colaboradoresDepartamento.map(col => (
                              <label key={col.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${participantes.includes(col.id) ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                <input type="checkbox" checked={participantes.includes(col.id)} onChange={() => handleToggleParticipante(col.id)} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" disabled={!canEditStep} />
                                <div>
                                  <div className="font-medium text-gray-900 text-xs">{col.nome}</div>
                                  <div className="text-[10px] text-gray-500">{col.cargo}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </section>

                    {incluirOutrosDepartamentos && (
                      <section className="bg-white rounded-xl border border-blue-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-blue-100 bg-blue-50/50 flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-blue-800">Outros Departamentos</h3>
                          <button onClick={() => handleToggleOutrosDepartamentos(false)} className="text-[10px] text-blue-600 hover:text-blue-800" disabled={!canEditStep}>Ocultar</button>
                        </div>
                        <div className="p-4 space-y-3">
                          <select value={departamentoSelecionado} onChange={(e) => handleChangeDepartamentoSelecionado(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" disabled={!canEditStep}>
                            <option value="">Selecione um departamento...</option>
                            {departamentosDisponiveis.map(dep => <option key={dep.id} value={dep.id}>{dep.nome}</option>)}
                          </select>
                          {departamentoSelecionado && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {colaboradoresOutrosDepartamentos.map(col => (
                                <label key={col.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${participantes.includes(col.id) ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                  <input type="checkbox" checked={participantes.includes(col.id)} onChange={() => handleToggleParticipante(col.id)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" disabled={!canEditStep} />
                                  <div>
                                    <div className="font-medium text-gray-900 text-xs">{col.nome}</div>
                                    <div className="text-[10px] text-gray-500">{col.cargo}</div>
                                  </div>
                                </label>
                              ))}
                              {colaboradoresOutrosDepartamentos.length === 0 && <p className="text-xs text-gray-400 col-span-2 text-center py-2">Nenhum colaborador ativo</p>}
                            </div>
                          )}
                          {Object.keys(participantesPorDepartamento).length > 0 && (
                            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                              <h6 className="text-[10px] font-semibold text-green-800 mb-2">Selecionados de outros departamentos:</h6>
                              {Object.entries(participantesPorDepartamento).map(([depNome, colabs]) => (
                                <div key={depNome} className="mb-1.5">
                                  <div className="text-[10px] font-medium text-gray-600 mb-0.5">{depNome}:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {colabs.map(col => (
                                      <label key={col.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[10px] cursor-pointer hover:bg-green-200">
                                        <input type="checkbox" checked={true} onChange={() => handleToggleParticipante(col.id)} className="h-3 w-3 rounded" disabled={!canEditStep} />
                                        {col.nome}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ============ ABA CAMPOS ============ */}
            {abaAtiva === 'campos' && (
              <div className="space-y-4">
                {/* Toggle do campo Título padrão */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={mostrarCampoTitulo} onChange={(e) => setMostrarCampoTitulo(e.target.checked)} className="sr-only peer" disabled={!canEditStep} />
                        <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                      <div>
                        <span className="text-sm font-medium text-gray-800">⭐ Campo Título padrão</span>
                        <p className="text-[10px] text-gray-400">
                          {mostrarCampoTitulo
                            ? 'Ativado — o formulário de novo cartão exibe o campo "Título" fixo'
                            : 'Desativado — marque um campo de texto como "Usar como Título" ou os cards ficarão sem título'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Layout principal: 2 colunas (campos + novo campo) */}
                <div className={`grid gap-4 ${expandirNovoCampo ? 'grid-cols-1 lg:grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>
                  {/* COLUNA ESQUERDA: Lista de campos */}
                  <div className="space-y-4 min-w-0">
                    {campos.length > 0 && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-purple-800">{campos.length} campo{campos.length > 1 ? 's' : ''} configurado{campos.length > 1 ? 's' : ''}</h4>
                          <button onClick={() => setExpandirCampos(!expandirCampos)} className="text-xs px-3 py-1 bg-white text-purple-700 hover:bg-purple-50 border border-purple-300 rounded-lg transition-colors">
                            {expandirCampos ? 'Fechar Editor' : 'Gerenciar Campos'}
                          </button>
                        </div>
                        {!expandirCampos && (
                          <div className="flex flex-wrap gap-1.5">
                            {campos.map(campo => (
                              <span key={campo.id} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${campo.obrigatorio ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-purple-100 text-purple-800'}`}>
                                {campo.nome}{campo.obrigatorio && <span className="ml-0.5 text-red-600">*</span>}
                                <span className="ml-1 text-gray-400">({getTipoNomeAmigavel(campo.tipo)})</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Gerenciar Campos (DnD Editor) */}
                    {expandirCampos && (
                      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-gray-800">Gerenciar Campos <span className="text-xs text-gray-400 font-normal">(arraste para reordenar)</span></h3>
                          <button onClick={() => setExpandirCampos(false)} className="text-xs text-gray-500 hover:text-gray-700">Fechar</button>
                        </div>
                    <div className="p-4">
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndCampos}>
                        <SortableContext items={campos.map(c => c.id)} strategy={verticalListSortingStrategy}>
                          <div className="space-y-2">
                            {campos.map(campo => (
                              <SortableItem key={campo.id} campo={campo}>
                                {editandoCampo === campo.id && campoEditando ? (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Nome do Campo</label>
                                      <input value={campoEditando.nome} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, nome: e.target.value } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Nome do campo" />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Tipo do Campo</label>
                                      <select value={campoEditando.tipo} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, tipo: e.target.value as CampoConfig['tipo'], opcoes: [] } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                        <option value="texto">📝 Texto</option>
                                        <option value="texto_longo">📄 Texto Longo</option>
                                        <option value="numero">🔢 Número (apenas números)</option>
                                        <option value="data">📅 Data e Horário</option>
                                        <option value="data_vencimento">⏰ Data de Vencimento</option>
                                        <option value="lista">📋 Lista de Seleção</option>
                                        <option value="progresso">☑️ Lista de Tarefas com Progresso</option>
<option value="parcelas">💳 Parcelas de pagamento</option>
<option value="itens">🧾 Itens da solicitação (por fornecedor)</option>
<option value="responsavel">👤 Responsável (pessoas do fluxo)</option>
                                        <option value="checkbox_single">🔘 Checkbox (única seleção)</option>
                                        <option value="checkbox_multi">✅ Checkbox (múltipla seleção)</option>
                                        <option value="justificativa_checkbox">💬 Justificativa de Checkbox</option>
                                        <option value="valor">💰 Valor em R$</option>
                                        <option value="telefone">📞 Telefone</option>
                                        <option value="anexo">📎 Anexo</option>
                                        <option value="rotas">✈️ Rotas de Viagem</option>
                                        <option value="rota_terrestre">🚗 Rota Terrestre</option>
                                        <option value="texto_informativo">🏷️ Texto Informativo (Label)</option>
                                      </select>
                                    </div>
                                    {campoEditando.tipo === 'justificativa_checkbox' && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Campo Checkbox Vinculado *</label>
                                        <select value={campoEditando.campoVinculadoId || ''} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, campoVinculadoId: e.target.value } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                                          <option value="">Selecione um campo checkbox...</option>
                                          {campos.filter(c => ['checkbox_multi','checkbox','checkbox_single'].includes(c.tipo)).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                                        </select>
                                      </div>
                                    )}
                                    {['lista','checkbox','checkbox_single','checkbox_multi','progresso','parcelas'].includes(campoEditando.tipo) && (
                                      <div className="space-y-2">
                                        <label className="block text-xs font-medium text-gray-700">{campoEditando.tipo === 'progresso' ? 'Tarefas' : campoEditando.tipo === 'parcelas' ? 'Parcelas padrão (quem cria o card pode mudar)' : 'Opções'}</label>
                                        <div className="flex gap-2">
                                          <input value={novaOpcaoEdicao} onChange={(e) => setNovaOpcaoEdicao(e.target.value)} placeholder={campoEditando.tipo === 'progresso' ? 'Nova tarefa' : campoEditando.tipo === 'parcelas' ? 'Nova parcela (ex.: 50% entrada)' : 'Nova opção'} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg" onKeyUp={(e) => e.key === 'Enter' && adicionarOpcaoEdicao()} />
                                          <button onClick={adicionarOpcaoEdicao} type="button" className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-xs">+</button>
                                        </div>
                                        <p className="text-[10px] text-gray-400">Cole múltiplas opções separadas por ; ou quebra de linha</p>
                                        {campoEditando.opcoes && campoEditando.opcoes.length > 0 && (
                                          <>
                                            <EditorOpcoesCampo
                                              opcoes={campoEditando.opcoes}
                                              coresOpcoes={campoEditando.coresOpcoes}
                                              mostrarCores={campoEditando.tipo !== 'progresso'}
                                              onChange={(opcoes, coresOpcoes) => setCampoEditando(prev => prev ? { ...prev, opcoes, coresOpcoes } : null)}
                                            />
                                            <p className="text-[10px] text-gray-400">
                                              Use ▲▼ para ordenar. Renomear aqui não altera o valor já gravado nos cards antigos.
                                            </p>
                                          </>
                                        )}
                                      </div>
                                    )}
                                    {campoEditando.tipo === 'anexo' && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Integração Padrão</label>
                                        <SeletorIntegracao selectedId={campoEditando.integracaoId || null} onSelect={(integracaoId) => setCampoEditando(prev => prev ? { ...prev, integracaoId: integracaoId || undefined } : null)} showLabel={false} />
                                      </div>
                                    )}
                                    {campoEditando.tipo === 'rota_terrestre' && (
                                      <div className="grid grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">⛽ Consumo médio (km/l)</label>
                                          <input type="number" step="0.1" min="1" value={campoEditando.consumoMedioPadrao || 10} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, consumoMedioPadrao: parseFloat(e.target.value) || 10 } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                          <p className="text-[10px] text-gray-400 mt-0.5">Padrão: 10 km/l</p>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">💲 Preço combustível (R$/l)</label>
                                          <input type="number" step="0.01" min="0.01" value={campoEditando.precoCombustivelPadrao || 6.0} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, precoCombustivelPadrao: parseFloat(e.target.value) || 6.0 } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                          <p className="text-[10px] text-gray-400 mt-0.5">Padrão: R$ 6,00/l</p>
                                        </div>
                                      </div>
                                    )}
                                    {campoEditando.tipo === 'texto_informativo' && (
                                      <div className="space-y-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">📝 Texto a exibir</label>
                                          <textarea value={campoEditando.textoInformativo || ''} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, textoInformativo: e.target.value } : null)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" rows={3} placeholder="Digite o texto informativo que será exibido..." />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">🎨 Formatação</label>
                                          <div className="flex flex-wrap gap-2 mb-2">
                                            <button type="button" onClick={() => setCampoEditando(prev => prev ? { ...prev, formatacao: { ...prev.formatacao, negrito: !prev.formatacao?.negrito } } : null)} className={`px-2.5 py-1 text-xs rounded-lg border ${campoEditando.formatacao?.negrito ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}><strong>N</strong></button>
                                            <button type="button" onClick={() => setCampoEditando(prev => prev ? { ...prev, formatacao: { ...prev.formatacao, italico: !prev.formatacao?.italico } } : null)} className={`px-2.5 py-1 text-xs rounded-lg border ${campoEditando.formatacao?.italico ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}><em>I</em></button>
                                          </div>
                                          <div className="grid grid-cols-2 gap-2">
                                            <div>
                                              <label className="block text-[10px] text-gray-500 mb-0.5">Tamanho</label>
                                              <select value={campoEditando.formatacao?.tamanho || 'medio'} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, formatacao: { ...prev.formatacao, tamanho: e.target.value as any } } : null)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg">
                                                <option value="pequeno">Pequeno</option>
                                                <option value="medio">Médio</option>
                                                <option value="grande">Grande</option>
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-[10px] text-gray-500 mb-0.5">Alinhamento</label>
                                              <select value={campoEditando.formatacao?.alinhamento || 'esquerda'} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, formatacao: { ...prev.formatacao, alinhamento: e.target.value as any } } : null)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg">
                                                <option value="esquerda">Esquerda</option>
                                                <option value="centro">Centro</option>
                                                <option value="direita">Direita</option>
                                              </select>
                                            </div>
                                          </div>
                                          <div className="mt-2">
                                            <label className="block text-[10px] text-gray-500 mb-0.5">Cor do texto</label>
                                            <div className="flex gap-1.5 flex-wrap">
                                              {[{cor: '#374151', nome: 'Cinza'}, {cor: '#1d4ed8', nome: 'Azul'}, {cor: '#059669', nome: 'Verde'}, {cor: '#dc2626', nome: 'Vermelho'}, {cor: '#d97706', nome: 'Laranja'}, {cor: '#7c3aed', nome: 'Roxo'}].map(c => (
                                                <button key={c.cor} type="button" onClick={() => setCampoEditando(prev => prev ? { ...prev, formatacao: { ...prev.formatacao, cor: c.cor } } : null)} className={`w-6 h-6 rounded-full border-2 ${campoEditando.formatacao?.cor === c.cor ? 'border-gray-900 ring-2 ring-gray-300' : 'border-gray-200'}`} style={{ backgroundColor: c.cor }} title={c.nome} />
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                        <label className="flex items-center gap-2 text-xs text-gray-700 mt-1">
                                          <input type="checkbox" checked={campoEditando.ocultarTitulo || false} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, ocultarTitulo: e.target.checked } : null)} className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600" />
                                          👁️‍🗨️ Ocultar título do campo
                                        </label>
                                        {campoEditando.textoInformativo && (
                                          <div>
                                            <label className="block text-[10px] text-gray-500 mb-1">Pré-visualização</label>
                                            <div className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg ${campoEditando.formatacao?.alinhamento === 'centro' ? 'text-center' : campoEditando.formatacao?.alinhamento === 'direita' ? 'text-right' : 'text-left'}`}>
                                              <span style={{ fontWeight: campoEditando.formatacao?.negrito ? 'bold' : 'normal', fontStyle: campoEditando.formatacao?.italico ? 'italic' : 'normal', fontSize: campoEditando.formatacao?.tamanho === 'pequeno' ? '11px' : campoEditando.formatacao?.tamanho === 'grande' ? '16px' : '13px', color: campoEditando.formatacao?.cor || '#374151' }}>{campoEditando.textoInformativo}</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <label className="flex items-center gap-2 text-xs text-gray-700">
                                      <input type="checkbox" checked={campoEditando.obrigatorio} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, obrigatorio: e.target.checked } : null)} className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600" />
                                      Campo Obrigatório
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-gray-700" title="Os demais veem o valor, mas não conseguem alterar">
                                      <input type="checkbox" checked={campoEditando.somenteAdmin || false} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, somenteAdmin: e.target.checked } : null)} className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600" />
                                      🔒 Só administradores editam
                                    </label>
                                    {campoEditando.tipo === 'anexo' && (
                                      <div className="grid grid-cols-2 gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                        <div>
                                          <label className="block text-[10px] text-gray-500 mb-0.5">Mínimo de anexos</label>
                                          <input
                                            type="number"
                                            min={0}
                                            value={campoEditando.minAnexos ?? 0}
                                            onChange={(e) => {
                                              const n = Math.max(0, parseInt(e.target.value, 10) || 0);
                                              setCampoEditando(prev => prev ? { ...prev, minAnexos: n, obrigatorio: n > 0 ? true : prev.obrigatorio } : null);
                                            }}
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-[10px] text-gray-500 mb-0.5">Máximo de anexos</label>
                                          <input
                                            type="number"
                                            min={1}
                                            value={campoEditando.maxAnexos ?? 6}
                                            onChange={(e) => setCampoEditando(prev => prev ? { ...prev, maxAnexos: Math.max(1, parseInt(e.target.value, 10) || 1) } : null)}
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                                          />
                                        </div>
                                        <p className="col-span-2 text-[10px] text-gray-500">
                                          {(campoEditando.minAnexos ?? 0) > 0
                                            ? `Só deixa enviar com pelo menos ${campoEditando.minAnexos} arquivo(s) neste campo.`
                                            : 'Sem mínimo: o campo aceita ser enviado vazio (a menos que esteja marcado como obrigatório).'}
                                        </p>
                                      </div>
                                    )}
                                    {['texto', 'texto_longo'].includes(campoEditando.tipo) && (
                                      <label className="flex items-center gap-2 text-xs text-gray-700" title="O valor é comparado com todos os cards dos fluxos que têm esta opção ligada. Não impede salvar.">
                                        <input type="checkbox" checked={!!campoEditando.validarFornecedorNovo} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, validarFornecedorNovo: e.target.checked } : null)} className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600" />
                                        Avisar quando for um fornecedor novo no Escalone
                                      </label>
                                    )}
                                    {['texto', 'texto_longo'].includes(campoEditando.tipo) && (
                                      <label className="flex items-center gap-2 text-xs text-gray-700">
                                        <input type="checkbox" checked={campoEditando.usarComoTitulo || false} onChange={(e) => setCampoEditando(prev => prev ? { ...prev, usarComoTitulo: e.target.checked } : null)} className="h-3.5 w-3.5 rounded border-gray-300 text-amber-500" />
                                        ⭐ Usar como Título do Cartão
                                      </label>
                                    )}
                                    <div className="flex gap-2 pt-1">
                                      <button onClick={salvarEdicaoCampo} type="button" className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"><CheckIcon className="h-3 w-3" />Salvar</button>
                                      <button onClick={cancelarEdicaoCampo} type="button" className="px-3 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-xs">Cancelar</button>
                                    </div>
                                  </div>
                                ) : campo.tipo === 'documento' ? (
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">📄</span>
                                      <div>
                                        <div className="font-medium text-gray-900 text-sm">{campo.nome}</div>
                                        <div className="text-xs text-blue-600">Documento vinculado</div>
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <button onClick={() => {
                                        const tid = campo.documentoTemplateId || campo.id.replace('doc_', '');
                                        setCampos(prev => prev.filter(c => c.id !== campo.id));
                                        setDocumentosHabilitados(p => p.filter(id => id !== tid));
                                      }} type="button" className="text-red-500 hover:bg-red-50 p-1 rounded-lg" title="Remover documento"><TrashIcon className="h-3.5 w-3.5" /></button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm">{campo.nome}{campo.obrigatorio && <span className="text-red-500 ml-1">*</span>}</div>
                                      <div className="text-xs text-gray-500">
                                        {getTipoNomeAmigavel(campo.tipo)}
                                        {['lista','checkbox','checkbox_single','checkbox_multi','progresso','parcelas'].includes(campo.tipo) && campo.opcoes && campo.opcoes.length > 0 && (
                                          <span className="ml-2">({campo.opcoes.length} {campo.tipo === 'progresso' ? 'tarefas' : campo.tipo === 'parcelas' ? 'parcelas padrão' : 'opções'})</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 relative">
                                      {(userPermissions?.canAdmin || checkEditorFieldPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'edit', campo.createdBy)) && (
                                        <button onClick={() => iniciarEdicaoCampo(campo)} type="button" className="text-blue-600 hover:bg-blue-50 p-1 rounded-lg" title="Editar"><PencilIcon className="h-3.5 w-3.5" /></button>
                                      )}
                                      {(userPermissions?.canAdmin || checkEditorFieldPermissions(userPermissions?.currentUser?.role || 'viewer', userPermissions?.currentUser?.id || '', flow.createdBy || flow.responsavelId, 'delete', campo.createdBy)) && (
                                        <button onClick={() => removerCampo(campo.id)} type="button" className="text-red-500 hover:bg-red-50 p-1 rounded-lg" title="Excluir"><TrashIcon className="h-3.5 w-3.5" /></button>
                                      )}
                                      {canAddFields && (
                                        <>
                                          <button onClick={() => setMenuCampoAberto(menuCampoAberto === campo.id ? null : campo.id)} type="button" className="text-gray-500 hover:bg-gray-100 p-1 rounded-lg" title="Mais"><EllipsisVerticalIcon className="h-4 w-4" /></button>
                                          {menuCampoAberto === campo.id && (
                                            <div className="absolute right-0 top-7 z-10 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                              <button onClick={() => clonarCampo(campo.id)} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Clonar campo</button>
                                              {['lista','checkbox_single','checkbox_multi'].includes(campo.tipo) && (
                                                <>
                                                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400 border-t border-gray-100">Converter para:</div>
                                                  {campo.tipo !== 'lista' && <button onClick={() => converterTipoCampo(campo.id, 'lista')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Lista de Seleção</button>}
                                                  {campo.tipo !== 'checkbox_single' && <button onClick={() => converterTipoCampo(campo.id, 'checkbox_single')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Checkbox (única)</button>}
                                                  {campo.tipo !== 'checkbox_multi' && <button onClick={() => converterTipoCampo(campo.id, 'checkbox_multi')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Checkbox (múltipla)</button>}
                                                </>
                                              )}
                                              {campo.tipo === 'anexo' && <button onClick={() => { setMenuCampoAberto(null); iniciarEdicaoCampo(campo); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100">Configurar integração</button>}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </SortableItem>
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                      </section>
                    )}

                    {/* Adicionar / Importar (botoes) */}
                    {canAddFields && (
                      <div className="flex gap-2">
                        <button onClick={() => setExpandirNovoCampo(!expandirNovoCampo)} className={`flex-1 px-4 py-2.5 border-2 border-dashed rounded-xl transition-colors text-sm font-medium ${expandirNovoCampo ? 'border-green-400 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600'}`}>{expandirNovoCampo ? 'Editando novo campo →' : '+ Adicionar Novo Campo'}</button>
                        <button onClick={() => setModalImportarAberto(true)} className="px-4 py-2.5 border-2 border-dashed border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 rounded-xl transition-colors text-sm font-medium">Importar</button>
                      </div>
                    )}

                  </div>{/* end coluna esquerda */}

                  {/* COLUNA DIREITA: Formulario Novo Campo (aparece lado a lado) */}
                  {expandirNovoCampo && canAddFields && (
                    <div className="min-w-0">
                      <section className="bg-white rounded-xl border-2 border-green-300 overflow-hidden sticky top-0 shadow-lg">
                        <div className="px-4 py-3 border-b border-green-100 bg-gradient-to-r from-green-50 to-emerald-50 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-green-600 flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                            </div>
                            <h3 className="text-sm font-bold text-green-800">Novo Campo</h3>
                          </div>
                          <button onClick={() => setExpandirNovoCampo(false)} className="text-xs text-green-600 hover:text-green-800 hover:bg-green-100 px-2 py-1 rounded-md transition-colors">Fechar</button>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Nome</label>
                            <input value={novoCampo.nome} onChange={(e) => setNovoCampo({ ...novoCampo, nome: e.target.value })} placeholder="Ex: Status da aprovacao" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" autoFocus />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Tipo</label>
                            <select value={novoCampo.tipo} onChange={(e) => setNovoCampo({ ...novoCampo, tipo: e.target.value as CampoConfig['tipo'], opcoes: [] })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                              <option value="texto">📝 Texto</option>
                              <option value="texto_longo">📄 Texto Longo</option>
                              <option value="numero">🔢 Número (apenas números)</option>
                              <option value="data">📅 Data e Horario</option>
                              <option value="data_vencimento">⏰ Data de Vencimento</option>
                              <option value="lista">📋 Lista de Selecao</option>
                              <option value="progresso">☑️ Lista de Tarefas com Progresso</option>
<option value="parcelas">💳 Parcelas de pagamento</option>
<option value="itens">🧾 Itens da solicitação (por fornecedor)</option>
<option value="responsavel">👤 Responsável (pessoas do fluxo)</option>
                              <option value="checkbox_single">🔘 Checkbox (unica selecao)</option>
                              <option value="checkbox_multi">✅ Checkbox (multipla selecao)</option>
                              <option value="justificativa_checkbox">💬 Justificativa de Checkbox</option>
                              <option value="valor">💰 Valor em R$</option>
                              <option value="telefone">📞 Telefone</option>
                              <option value="anexo">📎 Anexo</option>
                              <option value="rotas">✈️ Rotas de Viagem</option>
                              <option value="rota_terrestre">🚗 Rota Terrestre</option>
                              <option value="texto_informativo">🏷️ Texto Informativo (Label)</option>
                            </select>
                          </div>
                          {novoCampo.tipo === 'justificativa_checkbox' && (
                            <div>
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">Campo Checkbox Vinculado *</label>
                              <select value={novoCampo.campoVinculadoId || ''} onChange={(e) => setNovoCampo({ ...novoCampo, campoVinculadoId: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                                <option value="">Selecione...</option>
                                {campos.filter(c => c.tipo === 'checkbox_multi' || c.tipo === 'checkbox' || c.tipo === 'checkbox_single').map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                              </select>
                            </div>
                          )}
                          {['lista','checkbox','checkbox_single','checkbox_multi','progresso','parcelas'].includes(novoCampo.tipo) && (
                            <div className="space-y-2">
                              <label className="block text-[11px] font-medium text-gray-600">{novoCampo.tipo === 'progresso' ? 'Tarefas' : novoCampo.tipo === 'parcelas' ? 'Parcelas padrao (quem cria o card pode mudar)' : 'Opcoes'}</label>
                              <div className="flex gap-2">
                                <input value={novaOpcao} onChange={(e) => setNovaOpcao(e.target.value)} placeholder={novoCampo.tipo === 'progresso' ? 'Nova tarefa' : novoCampo.tipo === 'parcelas' ? 'Nova parcela (ex.: 50% entrada)' : 'Nova opcao'} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg" onKeyUp={(e) => e.key === 'Enter' && adicionarOpcao()} />
                                <button onClick={adicionarOpcao} type="button" className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-bold">+</button>
                              </div>
                              <p className="text-[10px] text-gray-400">Separe com ; ou quebre linha para adicionar varias</p>
                              {novoCampo.opcoes && novoCampo.opcoes.length > 0 && (
                                <>
                                  <EditorOpcoesCampo
                                    opcoes={novoCampo.opcoes}
                                    coresOpcoes={novoCampo.coresOpcoes}
                                    mostrarCores={novoCampo.tipo !== 'progresso'}
                                    onChange={(opcoes, coresOpcoes) => setNovoCampo(prev => ({ ...prev, opcoes, coresOpcoes }))}
                                  />
                                  <p className="text-[10px] text-gray-400">Use ▲▼ para ordenar.</p>
                                </>
                              )}
                            </div>
                          )}
                          {novoCampo.tipo === 'anexo' && (
                            <div>
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">Integracao Padrao (Opcional)</label>
                              <SeletorIntegracao selectedId={novoCampo.integracaoId || null} onSelect={(integracaoId) => setNovoCampo({ ...novoCampo, integracaoId: integracaoId || undefined })} showLabel={false} />
                            </div>
                          )}
                          {novoCampo.tipo === 'rota_terrestre' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-1">⛽ Consumo médio (km/l)</label>
                                <input type="number" step="0.1" min="1" value={novoCampo.consumoMedioPadrao || 10} onChange={(e) => setNovoCampo({ ...novoCampo, consumoMedioPadrao: parseFloat(e.target.value) || 10 })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                <p className="text-[10px] text-gray-400 mt-0.5">Padrão: 10 km/l</p>
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-1">💲 Preço combustível (R$/l)</label>
                                <input type="number" step="0.01" min="0.01" value={novoCampo.precoCombustivelPadrao || 6.0} onChange={(e) => setNovoCampo({ ...novoCampo, precoCombustivelPadrao: parseFloat(e.target.value) || 6.0 })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                <p className="text-[10px] text-gray-400 mt-0.5">Padrão: R$ 6,00/l</p>
                              </div>
                            </div>
                          )}
                          {novoCampo.tipo === 'texto_informativo' && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-1">📝 Texto a exibir</label>
                                <textarea value={novoCampo.textoInformativo || ''} onChange={(e) => setNovoCampo({ ...novoCampo, textoInformativo: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" rows={3} placeholder="Digite o texto informativo que será exibido..." />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-1">🎨 Formatação</label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  <button type="button" onClick={() => setNovoCampo({ ...novoCampo, formatacao: { ...novoCampo.formatacao, negrito: !novoCampo.formatacao?.negrito } })} className={`px-2.5 py-1 text-xs rounded-lg border ${novoCampo.formatacao?.negrito ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}><strong>N</strong></button>
                                  <button type="button" onClick={() => setNovoCampo({ ...novoCampo, formatacao: { ...novoCampo.formatacao, italico: !novoCampo.formatacao?.italico } })} className={`px-2.5 py-1 text-xs rounded-lg border ${novoCampo.formatacao?.italico ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}><em>I</em></button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">Tamanho</label>
                                    <select value={novoCampo.formatacao?.tamanho || 'medio'} onChange={(e) => setNovoCampo({ ...novoCampo, formatacao: { ...novoCampo.formatacao, tamanho: e.target.value as any } })} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg">
                                      <option value="pequeno">Pequeno</option>
                                      <option value="medio">Médio</option>
                                      <option value="grande">Grande</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500 mb-0.5">Alinhamento</label>
                                    <select value={novoCampo.formatacao?.alinhamento || 'esquerda'} onChange={(e) => setNovoCampo({ ...novoCampo, formatacao: { ...novoCampo.formatacao, alinhamento: e.target.value as any } })} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg">
                                      <option value="esquerda">Esquerda</option>
                                      <option value="centro">Centro</option>
                                      <option value="direita">Direita</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <label className="block text-[10px] text-gray-500 mb-0.5">Cor do texto</label>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {[{cor: '#374151', nome: 'Cinza'}, {cor: '#1d4ed8', nome: 'Azul'}, {cor: '#059669', nome: 'Verde'}, {cor: '#dc2626', nome: 'Vermelho'}, {cor: '#d97706', nome: 'Laranja'}, {cor: '#7c3aed', nome: 'Roxo'}].map(c => (
                                      <button key={c.cor} type="button" onClick={() => setNovoCampo({ ...novoCampo, formatacao: { ...novoCampo.formatacao, cor: c.cor } })} className={`w-6 h-6 rounded-full border-2 ${novoCampo.formatacao?.cor === c.cor ? 'border-gray-900 ring-2 ring-gray-300' : 'border-gray-200'}`} style={{ backgroundColor: c.cor }} title={c.nome} />
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-xs text-gray-700 mt-1">
                                <input type="checkbox" checked={novoCampo.ocultarTitulo || false} onChange={(e) => setNovoCampo({ ...novoCampo, ocultarTitulo: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-green-600" />
                                👁️‍🗨️ Ocultar título do campo
                              </label>
                              {novoCampo.textoInformativo && (
                                <div>
                                  <label className="block text-[10px] text-gray-500 mb-1">Pré-visualização</label>
                                  <div className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg ${novoCampo.formatacao?.alinhamento === 'centro' ? 'text-center' : novoCampo.formatacao?.alinhamento === 'direita' ? 'text-right' : 'text-left'}`}>
                                    <span style={{ fontWeight: novoCampo.formatacao?.negrito ? 'bold' : 'normal', fontStyle: novoCampo.formatacao?.italico ? 'italic' : 'normal', fontSize: novoCampo.formatacao?.tamanho === 'pequeno' ? '11px' : novoCampo.formatacao?.tamanho === 'grande' ? '16px' : '13px', color: novoCampo.formatacao?.cor || '#374151' }}>{novoCampo.textoInformativo}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <label className="flex items-center gap-2 text-xs text-gray-700 py-1">
                            <input type="checkbox" checked={novoCampo.obrigatorio} onChange={(e) => setNovoCampo({ ...novoCampo, obrigatorio: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            Campo Obrigatorio
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-700 py-1" title="Os demais veem o valor, mas não conseguem alterar">
                            <input type="checkbox" checked={novoCampo.somenteAdmin || false} onChange={(e) => setNovoCampo({ ...novoCampo, somenteAdmin: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            🔒 Só administradores editam
                          </label>
                          {['texto', 'texto_longo'].includes(novoCampo.tipo) && (
                            <label className="flex items-center gap-2 text-xs text-gray-700 py-1">
                              <input type="checkbox" checked={novoCampo.usarComoTitulo || false} onChange={(e) => setNovoCampo({ ...novoCampo, usarComoTitulo: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                              ⭐ Usar como Título do Cartão
                            </label>
                          )}
                          <div className="flex gap-2 pt-2 border-t border-gray-100">
                            <button onClick={() => { adicionarCampo(); }} type="button" className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold shadow-sm">Adicionar Campo</button>
                            <button onClick={() => setExpandirNovoCampo(false)} type="button" className="px-3 py-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm border border-gray-200">Cancelar</button>
                          </div>
                        </div>
                      </section>
                    </div>
                  )}
                </div>{/* end grid 2 colunas */}

                {/* Campos Visíveis no Card */}
                {campos.length > 0 && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                      <h3 className="text-sm font-semibold text-gray-800">Campos visiveis no cartao</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Selecione quais campos aparecem + clique no cadeado para fixar sempre</p>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        {campos.map(campo => {
                          const isVisible = camposVisiveisTmp.includes(campo.id);
                          const isTravado = camposTravadosTmp.includes(campo.id);
                          return (
                            <div key={campo.id} className={`flex items-center gap-1.5 text-[11px] p-2 rounded-lg border transition-all ${isTravado ? 'border-blue-400 bg-blue-50' : isVisible ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'} hover:shadow-sm`}>
                              <input type="checkbox" checked={isVisible} onChange={(e) => {
                                const atual = new Set(camposVisiveisTmp);
                                e.target.checked ? atual.add(campo.id) : atual.delete(campo.id);
                                setCamposVisiveisTmp(Array.from(atual));
                                if (!e.target.checked && isTravado) { const t = new Set(camposTravadosTmp); t.delete(campo.id); setCamposTravadosTmp(Array.from(t)); }
                              }} className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                              <span className="text-gray-700 truncate flex-1" title={`${campo.nome} (${campo.tipo})`}>{campo.nome}</span>
                              {isVisible && (
                                <button type="button" onClick={() => { const t = new Set(camposTravadosTmp); isTravado ? t.delete(campo.id) : t.add(campo.id); setCamposTravadosTmp(Array.from(t)); }} className={`p-0.5 rounded transition-colors ${isTravado ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`} title={isTravado ? 'Destrancar' : 'Travar - aparece sempre'}>
                                  {isTravado ? <LockClosedIcon className="h-3.5 w-3.5" /> : <LockOpenIcon className="h-3.5 w-3.5" />}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {camposTravadosTmp.length > 0 && (
                        <p className="mt-3 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2">
                          <LockClosedIcon className="h-3 w-3 inline mr-1" /><strong>{camposTravadosTmp.length}</strong> campo(s) travado(s) - aparecerão sempre
                        </p>
                      )}
                    </div>
                  </section>
                )}

                {/* Puxar Campos de Outras Etapas */}
                {flow?.etapas && flow.etapas.filter(e => e.id !== etapa.id).some(e => e.campos && e.campos.length > 0) && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800">Puxar campos de outras etapas</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">Exibir campos preenchidos em outras etapas</p>
                      </div>
                      {camposOutrasEtapas.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">{camposOutrasEtapas.length} selecionado(s)</span>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        onChange={(e) => {
                          const id = e.target.value;
                          flow.etapas.filter(et => et.id !== etapa.id).forEach(et => {
                            const el = document.getElementById(`campos-etapa-${et.id}`);
                            if (el) el.classList.add('hidden');
                          });
                          if (id) { const el = document.getElementById(`campos-etapa-${id}`); if (el) el.classList.remove('hidden'); }
                        }}
                        defaultValue=""
                      >
                        <option value="">Selecione uma etapa para ver campos...</option>
                        {flow.etapas.filter(e => e.id !== etapa.id && e.campos && e.campos.length > 0).map(outraEtapa => {
                          const sel = camposOutrasEtapas.filter(c => c.startsWith(`${outraEtapa.id}:`)).length;
                          return <option key={outraEtapa.id} value={outraEtapa.id}>{outraEtapa.nome} ({outraEtapa.campos?.length} campos){sel > 0 ? ` - ${sel} selecionado(s)` : ''}</option>;
                        })}
                      </select>
                      {flow.etapas.filter(e => e.id !== etapa.id && e.campos && e.campos.length > 0).map(outraEtapa => {
                        const temSelecionado = camposOutrasEtapas.some(c => c.startsWith(`${outraEtapa.id}:`));
                        return (
                          <div key={outraEtapa.id} id={`campos-etapa-${outraEtapa.id}`} className={temSelecionado ? '' : 'hidden'}>
                            <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-3">
                              <p className="text-xs font-medium text-purple-700 mb-2">{outraEtapa.nome}</p>
                              <div className="grid grid-cols-2 gap-1.5">
                                {outraEtapa.campos?.map(campoOutra => {
                                  const campoKey = `${outraEtapa.id}:${campoOutra.id}`;
                                  const isSelecionado = camposOutrasEtapas.includes(campoKey);
                                  return (
                                    <label key={campoKey} className={`flex items-center gap-1.5 text-[11px] p-2 rounded-lg cursor-pointer transition-all ${isSelecionado ? 'bg-purple-100 text-purple-800 border border-purple-300' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`}>
                                      <input type="checkbox" checked={isSelecionado} onChange={(e) => { const a = new Set(camposOutrasEtapas); e.target.checked ? a.add(campoKey) : a.delete(campoKey); setCamposOutrasEtapas(Array.from(a)); }} className="h-3 w-3 rounded border-gray-300 text-purple-600" />
                                      <span className="truncate">{campoOutra.nome}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* 💰 Cotação com fornecedor: o que o pedido leva por padrão */}
                {campos.length > 0 && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setCotacaoAberto(!cotacaoAberto)}
                      className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 text-left">💰 Cotação com fornecedor</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Campos que já vêm marcados ao pedir cotação a partir desta etapa
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {(cotacaoContexto.length > 0 || cotacaoResposta.length > 0 || cotacaoFornecedores.length > 0) && (
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                            {cotacaoContexto.length} vê · {cotacaoResposta.length} preenche
                            {cotacaoFornecedores.length > 0 && ` · ${cotacaoFornecedores.length} fornecedor(es)`}
                          </span>
                        )}
                        <span className={`text-gray-400 transition-transform ${cotacaoAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                      </div>
                    </button>

                    {cotacaoAberto && (
                      <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-3">
                        {!flow.configuracaoCotacao?.habilitado && (
                          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                            A cotação está <strong>desligada</strong> neste fluxo, então o botão não aparece nos cards.
                            Ligue em <strong>Configuração de Permissões · Cotação com fornecedor</strong> — o que você
                            marcar aqui fica salvo do mesmo jeito.
                          </p>
                        )}
                        <p className="text-[11px] text-gray-600">
                          <b>Vê</b> é o contexto que o fornecedor lê para conseguir orçar — pode vir de qualquer
                          etapa do fluxo (numa viagem, o viajante e o destino ficam na solicitação).
                          <b> Preenche</b> é o que ele devolve, e só sai desta etapa: é nesses campos que a
                          resposta é gravada.
                        </p>

                        {/* Fornecedor é quase sempre o mesmo; redigitar nome,
                            e-mail e telefone a cada pedido é trabalho à toa. */}
                        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-[11px] font-semibold text-gray-800">Fornecedores habituais</p>
                              <p className="text-[10px] text-gray-500">
                                O pedido já abre com eles. Quem cota pode editar ou apagar na hora, sem mexer aqui.
                              </p>
                            </div>
                            {canEditStep && (
                              <button
                                type="button"
                                onClick={() => setCotacaoFornecedores(prev => [...prev, { nome: '', email: '', telefone: '' }])}
                                className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                              >+ Fornecedor</button>
                            )}
                          </div>

                          {cotacaoFornecedores.length === 0 ? (
                            <p className="text-[11px] text-gray-400">
                              Nenhum salvo — o pedido abre com a lista em branco.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {cotacaoFornecedores.map((f, i) => (
                                <div key={i} className="grid grid-cols-1 md:grid-cols-[1.2fr_1.2fr_1fr_auto] gap-2">
                                  <input
                                    value={f.nome}
                                    disabled={!canEditStep}
                                    onChange={e => setCotacaoFornecedores(prev => prev.map((x, k) => k === i ? { ...x, nome: e.target.value } : x))}
                                    placeholder="Nome do fornecedor *"
                                    className="px-2.5 py-1.5 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50"
                                  />
                                  <input
                                    value={f.email || ''}
                                    disabled={!canEditStep}
                                    onChange={e => setCotacaoFornecedores(prev => prev.map((x, k) => k === i ? { ...x, email: e.target.value } : x))}
                                    placeholder="E-mail (recebe o link)"
                                    className="px-2.5 py-1.5 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50"
                                  />
                                  <input
                                    value={f.telefone || ''}
                                    disabled={!canEditStep}
                                    onChange={e => setCotacaoFornecedores(prev => prev.map((x, k) => k === i ? { ...x, telefone: e.target.value } : x))}
                                    placeholder="Telefone"
                                    className="px-2.5 py-1.5 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50"
                                  />
                                  {canEditStep && (
                                    <button
                                      type="button"
                                      onClick={() => setCotacaoFornecedores(prev => prev.filter((_, k) => k !== i))}
                                      className="px-2 text-gray-400 hover:text-red-500"
                                      title="Remover"
                                    >×</button>
                                  )}
                                </div>
                              ))}
                              {cotacaoFornecedores.some(f => !(f.email || '').trim()) && (
                                <p className="text-[10px] text-amber-700">
                                  Fornecedor sem e-mail não recebe o link automático — quem pedir vai ter que
                                  copiar e mandar na mão.
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        <SeletorCamposCotacao
                          etapaAtual={{ ...etapa, nome: nomeEtapa, campos }}
                          etapas={(flow.etapas || []).map(e =>
                            String(e.id) === String(etapa.id) ? { ...e, nome: nomeEtapa, campos } : e
                          )}
                          contexto={cotacaoContexto}
                          resposta={cotacaoResposta}
                          disabled={!canEditStep}
                          onChange={(c, r) => { setCotacaoContexto(c); setCotacaoResposta(r); }}
                        />

                        {cotacaoResposta.length === 0 && cotacaoContexto.length > 0 && (
                          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                            Nada marcado em <strong>Preenche</strong>: o pedido vai abrir com o contexto pronto,
                            mas quem for cotar ainda terá que marcar na mão o que o fornecedor devolve — sem isso
                            o botão "Gerar links" fica travado.
                          </p>
                        )}

                        <div className="flex items-center justify-between gap-3">
                          <p className="text-[10px] text-gray-500">
                            Quem pede a cotação ainda pode ajustar a seleção no card, sem mexer neste padrão.
                          </p>
                          {(cotacaoContexto.length > 0 || cotacaoResposta.length > 0) && canEditStep && (
                            <button
                              type="button"
                              onClick={() => { setCotacaoContexto([]); setCotacaoResposta([]); }}
                              className="text-[10px] px-2 py-1 rounded border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 whitespace-nowrap"
                            >
                              Limpar seleção
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {/* 🔪 Fatiar Card */}
                {campos.length > 0 && (
                  <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800">🔪 Fatiar card</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">Permite quebrar um card desta etapa em cards novos, escolhendo os campos na hora</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-none">
                        <input type="checkbox" checked={fatiarHabilitado} onChange={(e) => setFatiarHabilitado(e.target.checked)} className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                      </label>
                    </div>

                    {fatiarHabilitado && (
                      <div className="p-4 space-y-4">
                        {/* Marcar os campos e pronto. A fatia nasce nesta mesma etapa,
                            leva os anexos e herda todo o contexto do card de origem —
                            nada disso precisa de configuração. */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-[11px] font-medium text-gray-700">Campos que podem ser fatiados</label>
                            <span className="text-[10px] text-gray-400">
                              {fatiarCamposFatiaveis.length === 0 ? 'nenhum marcado = todos liberados' : `${fatiarCamposFatiaveis.length} liberado(s)`}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {campos.map(campo => {
                              const liberado = fatiarCamposFatiaveis.includes(campo.id);
                              return (
                                <label key={campo.id} className={`flex items-center gap-1.5 text-[11px] p-2 rounded-lg border cursor-pointer transition-all ${liberado ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                                  <input
                                    type="checkbox"
                                    checked={liberado}
                                    onChange={(e) => {
                                      const a = new Set(fatiarCamposFatiaveis);
                                      e.target.checked ? a.add(campo.id) : a.delete(campo.id);
                                      setFatiarCamposFatiaveis(Array.from(a));
                                    }}
                                    className="h-3 w-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                  />
                                  <span className="text-gray-700 truncate" title={campo.nome}>{campo.nome}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[11px] font-medium text-gray-700 mb-1">Texto do botão no card <span className="text-gray-400 font-normal">(opcional)</span></label>
                          <input
                            type="text"
                            value={fatiarRotulo}
                            onChange={(e) => setFatiarRotulo(e.target.value)}
                            placeholder="Fatiar card"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>

                        <div className="space-y-2">
                          <p className="text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                            A fatia nasce nesta mesma etapa, leva os anexos dos campos marcados e fica ligada ao card de origem. O título vem como <code className="bg-white px-1 rounded border border-gray-200">[FATIA 1] título do card</code> e dá para editar na hora de fatiar.
                          </p>
                          <p className="text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                            <strong>Quem pode fatiar:</strong> apenas administradores que sejam participantes desta etapa. Editores não fatiam, mesmo sendo participantes.
                            {participantes.length === 0 && ' Como esta etapa ainda não tem participantes, qualquer administrador poderá fatiar.'}
                          </p>
                          <p className="text-[10px] text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                            Os campos marcados <strong>saem</strong> do card de origem e passam a viver na fatia. Nada se perde: antes do corte o sistema grava um retrato completo do card no histórico de etapas, então dá para ver como ele estava e de onde a fatia veio.
                          </p>
                        </div>
                      </div>
                    )}
                  </section>
                )}


                {/* Sem campos */}
                {campos.length === 0 && !expandirNovoCampo && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
                    <p className="text-gray-400 text-sm mb-3">Nenhum campo configurado nesta etapa</p>
                    {canAddFields && (
                      <div className="flex gap-2 justify-center">
                        <button onClick={() => setExpandirNovoCampo(true)} className="text-xs px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">+ Adicionar Campo</button>
                        <button onClick={() => setModalImportarAberto(true)} className="text-xs px-4 py-2 bg-white text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">Importar</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ============ ABA FLUXO ============ */}
            {abaAtiva === 'fluxo' && (
              <div className="space-y-5">
                {/* Permitir Criação */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Permitir Criação de Cards</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Define se é possível criar novos cards nesta etapa</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={permiteCriarCards} onChange={(e) => setPermiteCriarCards(e.target.checked)} disabled={!canEditStep} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </section>

                {/* Reprovação / reenvio de anexos pelo portal */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Pedir reenvio de anexo ao solicitante</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Etapa de reprovação: ao mover um card para cá, você escolhe quais anexos estão errados.
                        O solicitante vê só esses campos no acompanhamento do portal.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                      <input type="checkbox" checked={solicitarReenvioAnexos} onChange={(e) => setSolicitarReenvioAnexos(e.target.checked)} disabled={!canEditStep} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>
                  {solicitarReenvioAnexos && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Quando o solicitante reenviar, mover o card para</label>
                      <select
                        value={etapaRetornoReenvioId}
                        onChange={(e) => setEtapaRetornoReenvioId(e.target.value)}
                        disabled={!canEditStep}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg"
                      >
                        <option value="">Voltar para a etapa de onde o card saiu</option>
                        {(flow.etapas || []).filter(ed => ed.id !== etapa.id).map(ed => (
                          <option key={ed.id} value={ed.id}>{ed.nome}</option>
                        ))}
                      </select>
                      <p className="text-[10px] text-gray-500 mt-1.5">
                        O card sai desta etapa sozinho assim que o arquivo novo chegar.
                      </p>

                      <label className="block text-xs font-medium text-gray-700 mt-4 mb-1">Etiqueta ao voltar</label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={etiquetaReenvioNome}
                          onChange={(e) => setEtiquetaReenvioNome(e.target.value)}
                          disabled={!canEditStep}
                          placeholder="Reanálise (deixe vazio para não marcar)"
                          className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg"
                        />
                        <input
                          type="color"
                          value={etiquetaReenvioCor}
                          onChange={(e) => setEtiquetaReenvioCor(e.target.value)}
                          disabled={!canEditStep || !etiquetaReenvioNome.trim()}
                          className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0 flex-shrink-0 disabled:opacity-40"
                          title="Cor da etiqueta"
                        />
                      </div>
                      {etiquetaReenvioNome.trim() ? (
                        <p className="text-[10px] text-gray-500 mt-1.5">
                          O card volta marcado com{' '}
                          <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style={{ backgroundColor: etiquetaReenvioCor }}>
                            {etiquetaReenvioNome.trim()}
                          </span>
                          {' '}— a etiqueta é criada no fluxo se ainda não existir.
                        </p>
                      ) : (
                        <p className="text-[10px] text-gray-500 mt-1.5">Sem etiqueta: o card volta sem marcação.</p>
                      )}
                    </div>
                  )}
                </section>

                {/* Movimentação */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setMovimentacaoAberto(!movimentacaoAberto)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 text-left">Movimentação de Cards</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Para quais etapas os cards podem ser movidos</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {permiteMoverPara.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">{permiteMoverPara.length} etapa{permiteMoverPara.length > 1 ? 's' : ''}</span>}
                      <span className={`text-gray-400 transition-transform ${movimentacaoAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                    </div>
                  </button>
                  {movimentacaoAberto && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                      {flow.etapas && flow.etapas.length > 1 ? (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                          {flow.etapas.filter(ed => ed.id !== etapa.id).map(ed => (
                            <label key={ed.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${permiteMoverPara.includes(ed.id) ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                              <input type="checkbox" checked={permiteMoverPara.includes(ed.id)} onChange={(e) => { e.target.checked ? setPermiteMoverPara(p => [...p, ed.id]) : setPermiteMoverPara(p => p.filter(id => id !== ed.id)); }} disabled={!canEditStep} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                              <div>
                                <div className="font-medium text-gray-900 text-xs">{ed.nome}</div>
                                {ed.departamentoId && <div className="text-[10px] text-gray-500">{departamentos.find(d => d.id === ed.departamentoId)?.nome}</div>}
                              </div>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 text-center py-3">Este fluxo tem apenas uma etapa</p>
                      )}
                    </div>
                  )}
                </section>

                {/* Documentos */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setDocumentosAberto(!documentosAberto)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" type="button">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 text-left">Documentos Disponíveis</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Templates para visualização e impressão</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {documentosHabilitados.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">{documentosHabilitados.length}</span>}
                      <span className={`text-gray-400 transition-transform ${documentosAberto ? 'rotate-180' : ''}`}>&#9660;</span>
                    </div>
                  </button>
                  {documentosAberto && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                      {loadingTemplates ? (
                        <div className="text-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto mb-2"></div><p className="text-xs text-gray-500">Carregando templates...</p></div>
                      ) : templatesDocumentos.length > 0 ? (
                        <div className="space-y-1.5">
                          {templatesDocumentos.map(template => {
                            const tid = template._id || template.id;
                            const isChecked = documentosHabilitados.includes(tid);
                            return (
                            <label key={tid} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all border ${isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                              <input type="checkbox" checked={isChecked} onChange={(e) => {
                                if (e.target.checked) {
                                  setDocumentosHabilitados(p => [...p, tid]);
                                  // Adicionar como campo documento no final da lista
                                  setCampos(prev => [...prev, {
                                    id: `doc_${tid}`,
                                    nome: template.nome,
                                    tipo: 'documento' as CampoConfig['tipo'],
                                    obrigatorio: false,
                                    documentoTemplateId: tid,
                                  }]);
                                } else {
                                  setDocumentosHabilitados(p => p.filter(id => id !== tid));
                                  // Remover campo documento da lista
                                  setCampos(prev => prev.filter(c => c.id !== `doc_${tid}`));
                                }
                              }} disabled={!canEditStep} className="h-4 w-4 mt-0.5 rounded border-gray-300 text-blue-600" />
                              <div>
                                <div className="font-medium text-gray-900 text-xs">{template.nome}</div>
                                {template.descricao && <div className="text-[10px] text-gray-500 mt-0.5">{template.descricao}</div>}
                                {template.etapaNome && <div className="text-[10px] text-blue-600 mt-0.5">Vinculado: {template.etapaNome}</div>}
                              </div>
                            </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 text-center py-3">Nenhum template de documento criado</p>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* ============ ABA NOTIFICAÇÕES ============ */}
            {abaAtiva === 'notificacoes' && (
              <div className="space-y-5">
                {/* E-mail */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Notificações por E-mail</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">E-mail automático quando um card entrar nesta etapa</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={notificacoesEmailHabilitado} onChange={(e) => {
                        setNotificacoesEmailHabilitado(e.target.checked);
                        if (e.target.checked && participantes.length > 0) {
                          const novos = { ...notificacoesEmailColaboradores };
                          participantes.forEach(pId => { if (!novos[pId]) novos[pId] = { receberNotificacao: true }; });
                          setNotificacoesEmailColaboradores(novos);
                        }
                      }} disabled={!canEditStep} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {notificacoesEmailHabilitado && participantes.length > 0 && (
                    <div className="p-5 space-y-2">
                      <h5 className="text-xs font-medium text-gray-700 mb-2">Gerenciar por Colaborador</h5>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto">
                        {participantes.map(pId => {
                          const col = colaboradores.find(c => c.id === pId);
                          if (!col) return null;
                          return (
                            <div key={pId} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                              <div>
                                <div className="font-medium text-gray-900 text-xs">{col.nome}</div>
                                <div className="text-[10px] text-gray-500">{col.email}</div>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={notificacoesEmailColaboradores[pId]?.receberNotificacao ?? true} onChange={(e) => setNotificacoesEmailColaboradores(prev => ({ ...prev, [pId]: { receberNotificacao: e.target.checked } }))} disabled={!canEditStep} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">E-mail com título do card, etapa e link de acesso</p>
                    </div>
                  )}
                  {notificacoesEmailHabilitado && participantes.length === 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Adicione participantes primeiro (aba "Equipe")</p></div>
                  )}
                </section>

                {/* WhatsApp */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Notificações por WhatsApp</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">WhatsApp automático quando um card entrar nesta etapa</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={notificacoesWhatsappHabilitado} onChange={(e) => {
                        setNotificacoesWhatsappHabilitado(e.target.checked);
                        if (e.target.checked && participantes.length > 0) {
                          const novos = { ...notificacoesWhatsappColaboradores };
                          participantes.forEach(pId => { if (!novos[pId]) novos[pId] = { receberNotificacao: true }; });
                          setNotificacoesWhatsappColaboradores(novos);
                        }
                      }} disabled={!canEditStep} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  {notificacoesWhatsappHabilitado && participantes.length > 0 && (
                    <div className="p-5 space-y-2">
                      <h5 className="text-xs font-medium text-gray-700 mb-2">Gerenciar por Colaborador</h5>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto">
                        {participantes.map(pId => {
                          const col = colaboradores.find(c => c.id === pId);
                          if (!col) return null;
                          const temTelefone = !!col.telefone;
                          return (
                            <div key={pId} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                              <div>
                                <div className="font-medium text-gray-900 text-xs">{col.nome}</div>
                                <div className={`text-[10px] ${temTelefone ? 'text-gray-500' : 'text-amber-500'}`}>{col.telefone || 'Sem telefone cadastrado'}</div>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={notificacoesWhatsappColaboradores[pId]?.receberNotificacao ?? true} onChange={(e) => setNotificacoesWhatsappColaboradores(prev => ({ ...prev, [pId]: { receberNotificacao: e.target.checked } }))} disabled={!canEditStep} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 mt-2">WhatsApp com título do card, etapa e link de acesso</p>
                    </div>
                  )}
                  {notificacoesWhatsappHabilitado && participantes.length === 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Adicione participantes primeiro (aba "Equipe")</p></div>
                  )}
                </section>

                {/* Alerta SLA WhatsApp */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Alerta SLA por WhatsApp</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">WhatsApp automático ao responsável quando o card atrasar o SLA</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={alertaSlaWhatsappHabilitado} onChange={(e) => setAlertaSlaWhatsappHabilitado(e.target.checked)} disabled={!canEditStep || tempoMaximoHoras <= 0} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  {tempoMaximoHoras <= 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Configure o SLA primeiro (aba "Geral" → Tempo Máximo)</p></div>
                  )}
                  {tempoMaximoHoras > 0 && alertaSlaWhatsappHabilitado && (
                    <div className="p-5">
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">Quando o card ultrapassar <strong>{tempoMaximoHoras}h</strong> nesta etapa, o responsável pelo card receberá um alerta no WhatsApp com o nome do card, etapa e tempo de atraso.</p>
                    </div>
                  )}
                </section>

                {/* Escalonamento de SLA por e-mail */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Escalonamento de SLA por e-mail</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Card que dobra, triplica... o prazo da etapa sobe a hierarquia</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={escalonamentoSlaHabilitado} onChange={(e) => setEscalonamentoSlaHabilitado(e.target.checked)} disabled={!canEditStep || tempoMaximoHoras <= 0} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>

                  {tempoMaximoHoras <= 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Configure o SLA primeiro (aba "Geral" → Tempo Máximo)</p></div>
                  )}

                  {tempoMaximoHoras > 0 && escalonamentoSlaHabilitado && (
                    <div className="p-5 space-y-4">
                      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                        <p><strong>{tempoMaximoHoras * 2}h</strong> na etapa (2x o prazo) → avisa o responsável do card, ou os participantes da etapa se não houver responsável. O card fica vermelho e pulsando no quadro.</p>
                        <p><strong>{tempoMaximoHoras * 3}h</strong> (3x) → o superior direto dele entra no e-mail.</p>
                        <p><strong>{tempoMaximoHoras * 4}h</strong> (4x) → sobe mais um nível, e assim por diante.</p>
                        <p className="text-red-600/80 pt-1">Quem já recebeu continua recebendo — é assim que o gestor enxerga que o aviso anterior não virou ação.</p>
                      </div>

                      {/* Destinatários fixos */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Sempre avisar também <span className="font-normal text-gray-500">(recebem em todos os níveis, fora da hierarquia)</span>
                        </label>

                        {/* Selecionados ficam aqui em cima: não somem quando a busca filtra a lista */}
                        {escalonamentoDestinatarios.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {escalonamentoDestinatarios.map(id => {
                              const col = colaboradores.find(c => c.id === id);
                              return (
                                <span key={id} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-red-50 text-red-700 border border-red-200 rounded-full">
                                  {col?.nome || id}
                                  {canEditStep && (
                                    <button
                                      type="button"
                                      onClick={() => setEscalonamentoDestinatarios(prev => prev.filter(x => x !== id))}
                                      className="text-red-400 hover:text-red-700"
                                      title="Remover"
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        <input
                          type="text"
                          value={escalonamentoBusca}
                          onChange={(e) => setEscalonamentoBusca(e.target.value)}
                          placeholder="Buscar por nome ou e-mail..."
                          className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />

                        {(() => {
                          // Busca sem acento: "braulio" acha "BRÁULIO"
                          const semAcento = (t: string) =>
                            (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
                          const termo = semAcento(escalonamentoBusca.trim());
                          const filtrados = termo
                            ? colaboradores.filter(c =>
                                semAcento(c.nome).includes(termo) || semAcento(c.email || '').includes(termo))
                            : colaboradores;

                          return (
                            <>
                              <div className="border border-gray-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-gray-100">
                                {colaboradores.length === 0 && (
                                  <p className="text-xs text-gray-400 p-3">Nenhum colaborador cadastrado.</p>
                                )}
                                {colaboradores.length > 0 && filtrados.length === 0 && (
                                  <p className="text-xs text-gray-400 p-3">Ninguém encontrado para "{escalonamentoBusca}".</p>
                                )}
                                {filtrados.map(col => (
                                  <label key={col.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="rounded"
                                      disabled={!canEditStep}
                                      checked={escalonamentoDestinatarios.includes(col.id)}
                                      onChange={(e) => setEscalonamentoDestinatarios(prev =>
                                        e.target.checked ? [...prev, col.id] : prev.filter(id => id !== col.id)
                                      )}
                                    />
                                    <span className="text-xs text-gray-800 flex-1 min-w-0 truncate">{col.nome}</span>
                                    {col.email && <span className="text-[10px] text-gray-400 truncate max-w-[45%]">{col.email}</span>}
                                  </label>
                                ))}
                              </div>

                              <p className="text-[10px] text-gray-500 mt-1">
                                {escalonamentoDestinatarios.length} selecionado(s)
                                {termo && ` · ${filtrados.length} de ${colaboradores.length} na busca`}
                              </p>
                            </>
                          );
                        })()}
                      </div>

                      {/* E-mails avulsos */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          E-mails avulsos <span className="font-normal text-gray-500">(alias de diretoria, alguém de fora, etc.)</span>
                        </label>

                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={escalonamentoEmailNovo}
                            onChange={(e) => setEscalonamentoEmailNovo(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter') return;
                              e.preventDefault();
                              const valor = escalonamentoEmailNovo.trim();
                              if (valor.includes('@') && !escalonamentoEmails.includes(valor)) {
                                setEscalonamentoEmails(prev => [...prev, valor]);
                                setEscalonamentoEmailNovo('');
                              }
                            }}
                            placeholder="diretoria@empresa.com.br"
                            disabled={!canEditStep}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                          />
                          <button
                            type="button"
                            disabled={!canEditStep || !escalonamentoEmailNovo.includes('@')}
                            onClick={() => {
                              const valor = escalonamentoEmailNovo.trim();
                              if (valor.includes('@') && !escalonamentoEmails.includes(valor)) {
                                setEscalonamentoEmails(prev => [...prev, valor]);
                                setEscalonamentoEmailNovo('');
                              }
                            }}
                            className="px-3 py-2 text-xs font-medium bg-gray-900 text-white rounded-lg disabled:opacity-40"
                          >
                            Adicionar
                          </button>
                        </div>

                        {escalonamentoEmails.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {escalonamentoEmails.map(email => (
                              <span key={email} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-100 text-gray-700 rounded-full">
                                {email}
                                {canEditStep && (
                                  <button
                                    type="button"
                                    onClick={() => setEscalonamentoEmails(prev => prev.filter(x => x !== email))}
                                    className="text-gray-400 hover:text-red-600"
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                {/* Campos por WhatsApp */}
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Campos por WhatsApp</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Preencher campos do card via conversa no WhatsApp</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={camposWhatsappHabilitado} onChange={(e) => {
                        setCamposWhatsappHabilitado(e.target.checked);
                        if (e.target.checked && participantes.length > 0) {
                          const novos = { ...camposWhatsappColaboradores };
                          participantes.forEach(pId => { if (!novos[pId]) novos[pId] = { receberNotificacao: true }; });
                          setCamposWhatsappColaboradores(novos);
                        }
                      }} disabled={!canEditStep} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  {camposWhatsappHabilitado && campos.length > 0 && (
                    <div className="p-5 space-y-4">
                      {/* Selecionar campos */}
                      <div>
                        <h5 className="text-xs font-medium text-gray-700 mb-2">Campos para perguntar</h5>
                        <p className="text-[10px] text-gray-500 mb-2">Selecione os campos que serão enviados como perguntas no WhatsApp</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {campos.filter(c => ['texto', 'texto_longo', 'valor', 'data', 'lista', 'checkbox_single', 'checkbox', 'checkbox_multi', 'numero', 'telefone'].includes(c.tipo)).map(campo => (
                            <div key={campo.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className="text-xs">{campo.nome}</span>
                                <span className="text-[10px] text-gray-400">{campo.tipo}</span>
                                {campo.obrigatorio && <span className="text-[10px] text-red-400">*</span>}
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={camposWhatsappSelecionados.includes(campo.id)} onChange={(e) => {
                                  if (e.target.checked) {
                                    setCamposWhatsappSelecionados(prev => [...prev, campo.id]);
                                  } else {
                                    setCamposWhatsappSelecionados(prev => prev.filter(id => id !== campo.id));
                                  }
                                }} disabled={!canEditStep} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                              </label>
                            </div>
                          ))}
                        </div>
                        {campos.filter(c => ['texto', 'texto_longo', 'valor', 'data', 'lista', 'checkbox_single', 'checkbox', 'checkbox_multi', 'numero', 'telefone'].includes(c.tipo)).length === 0 && (
                          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">Nenhum campo compatível nesta etapa (texto, número, data, lista, checkbox)</p>
                        )}
                      </div>
                      {/* Selecionar colaboradores */}
                      {participantes.length > 0 && camposWhatsappSelecionados.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-700 mb-2">Quem vai receber as perguntas</h5>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {participantes.map(pId => {
                              const col = colaboradores.find(c => c.id === pId);
                              if (!col) return null;
                              const temTelefone = !!col.telefone;
                              return (
                                <div key={pId} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                                  <div>
                                    <div className="font-medium text-gray-900 text-xs">{col.nome}</div>
                                    <div className={`text-[10px] ${temTelefone ? 'text-gray-500' : 'text-amber-500'}`}>{col.telefone || 'Sem telefone cadastrado'}</div>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={camposWhatsappColaboradores[pId]?.receberNotificacao ?? true} onChange={(e) => setCamposWhatsappColaboradores(prev => ({ ...prev, [pId]: { receberNotificacao: e.target.checked } }))} disabled={!canEditStep} className="sr-only peer" />
                                    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-2 mt-2">Quando um card entrar nesta etapa, os campos selecionados serão enviados como perguntas no WhatsApp. As respostas preenchem o card automaticamente.</p>
                    </div>
                  )}
                  {camposWhatsappHabilitado && campos.length === 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Adicione campos primeiro (aba "Campos")</p></div>
                  )}
                  {camposWhatsappHabilitado && participantes.length === 0 && campos.length > 0 && (
                    <div className="p-5"><p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-3">Adicione participantes primeiro (aba "Equipe")</p></div>
                  )}
                </section>
              </div>
            )}

            {/* ============ ABA CARDS PERSONALIZADOS ============ */}
            {abaAtiva === 'templates' && (
              <div className="space-y-5">
                <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Cards Personalizados</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">Crie modelos com campos pré-preenchidos. Ao criar um novo card nesta etapa, o usuário pode escolher um modelo para auto-preencher os campos.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!canEditSection('templates')) return;
                        const novo: CardTemplate = {
                          id: 'tpl_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8),
                          nome: 'Novo modelo',
                          descricao: '',
                          cor: '#6366F1',
                          titulo: '',
                          valores: {},
                          criadoEm: new Date().toISOString(),
                        };
                        setCardsPersonalizados(prev => [...prev, novo]);
                        setTemplateEditandoId(novo.id);
                        setTemplateDraft(novo);
                      }}
                      disabled={!canEditSection('templates')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${canEditSection('templates') ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                    >
                      <PlusIcon className="h-3.5 w-3.5" /> Novo modelo
                    </button>
                  </div>

                  <div className="p-5 space-y-3">
                    {campos.length === 0 && (
                      <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        Adicione campos primeiro (aba "Campos") antes de criar modelos.
                      </div>
                    )}

                    {campos.length > 0 && cardsPersonalizados.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-6">Nenhum modelo criado. Clique em "Novo modelo" para começar.</p>
                    )}

                    {cardsPersonalizados.map(tpl => {
                      const isEditing = templateEditandoId === tpl.id && templateDraft?.id === tpl.id;
                      const preenchidos = Object.keys(tpl.valores || {}).filter(k => {
                        const v = tpl.valores[k];
                        if (v === undefined || v === null || v === '') return false;
                        if (Array.isArray(v) && v.length === 0) return false;
                        return true;
                      }).length;

                      if (!isEditing) {
                        return (
                          <div key={tpl.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tpl.cor || '#6366F1' }} />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                  {tpl.nome || 'Sem nome'}
                                  {tpl.recorrencia?.ativo && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-semibold flex-shrink-0">
                                      <ArrowPathIcon className="h-2.5 w-2.5" /> Recorrente
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-gray-500 truncate">
                                  {tpl.descricao || `${preenchidos} campo(s) pré-preenchido(s)`}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => { setTemplateEditandoId(tpl.id); setTemplateDraft({ ...tpl, valores: { ...(tpl.valores || {}) } }); }}
                                disabled={!canEditSection('templates')}
                                className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-40"
                                title="Editar"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!canEditSection('templates')) return;
                                  const copia: CardTemplate = {
                                    ...tpl,
                                    id: 'tpl_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8),
                                    nome: (tpl.nome || 'Modelo') + ' (cópia)',
                                    valores: { ...(tpl.valores || {}) },
                                    criadoEm: new Date().toISOString(),
                                  };
                                  setCardsPersonalizados(prev => [...prev, copia]);
                                }}
                                disabled={!canEditSection('templates')}
                                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
                                title="Duplicar"
                              >
                                <DocumentDuplicateIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!canEditSection('templates')) return;
                                  if (!confirm(`Excluir o modelo "${tpl.nome}"?`)) return;
                                  setCardsPersonalizados(prev => prev.filter(t => t.id !== tpl.id));
                                }}
                                disabled={!canEditSection('templates')}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                                title="Excluir"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      }

                      // Modo edição
                      const draft = templateDraft!;
                      const setDraftValor = (campoId: string, valor: any) => {
                        setTemplateDraft(prev => prev ? { ...prev, valores: { ...prev.valores, [campoId]: valor } } : prev);
                      };
                      const salvarDraft = () => {
                        setCardsPersonalizados(prev => prev.map(t => t.id === draft.id ? { ...draft } : t));
                        setTemplateEditandoId(null);
                        setTemplateDraft(null);
                      };
                      const cancelarDraft = () => {
                        setTemplateEditandoId(null);
                        setTemplateDraft(null);
                      };

                      // Recorrência (criação automática)
                      const rec = draft.recorrencia;
                      const setRec = (patch: Partial<CardTemplateRecorrencia>) => {
                        setTemplateDraft(prev => {
                          if (!prev) return prev;
                          const base: CardTemplateRecorrencia = prev.recorrencia || { ativo: true, frequencia: 'mensal', hora: 9, minuto: 0, diaDoMes: 1, diaDaSemana: 1 };
                          return { ...prev, recorrencia: { ...base, ...patch } };
                        });
                      };
                      const toggleRec = () => {
                        setTemplateDraft(prev => {
                          if (!prev) return prev;
                          if (prev.recorrencia?.ativo) {
                            return { ...prev, recorrencia: { ...prev.recorrencia, ativo: false } };
                          }
                          const base: CardTemplateRecorrencia = prev.recorrencia
                            ? { ...prev.recorrencia, ativo: true }
                            : { ativo: true, frequencia: 'mensal', hora: 9, minuto: 0, diaDoMes: 1, diaDaSemana: 1 };
                          return { ...prev, recorrencia: base };
                        });
                      };
                      const resumoRec = (() => {
                        if (!rec?.ativo) return '';
                        const hh = String(rec.hora ?? 0).padStart(2, '0');
                        const mm = String(rec.minuto ?? 0).padStart(2, '0');
                        const dias = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
                        if (rec.frequencia === 'diario') return `Cria este card todo dia às ${hh}:${mm}.`;
                        if (rec.frequencia === 'semanal') return `Cria este card toda ${dias[rec.diaDaSemana ?? 1] || 'segunda-feira'} às ${hh}:${mm}.`;
                        return `Cria este card todo dia ${rec.diaDoMes ?? 1} de cada mês às ${hh}:${mm}.`;
                      })();

                      const mostrarTitulo = etapa.mostrarCampoTitulo !== false && !campos.some(c => c.usarComoTitulo);

                      return (
                        <div key={tpl.id} className="border-2 border-indigo-300 rounded-lg p-4 bg-indigo-50/30 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2">
                              <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Nome do modelo</label>
                              <input
                                value={draft.nome}
                                onChange={(e) => setTemplateDraft(prev => prev ? { ...prev, nome: e.target.value } : prev)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Ex.: Novo colaborador CLT"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Cor</label>
                              <input
                                type="color"
                                value={draft.cor || '#6366F1'}
                                onChange={(e) => setTemplateDraft(prev => prev ? { ...prev, cor: e.target.value } : prev)}
                                className="w-full h-9 border border-gray-300 rounded-lg cursor-pointer"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Descrição (opcional)</label>
                            <input
                              value={draft.descricao || ''}
                              onChange={(e) => setTemplateDraft(prev => prev ? { ...prev, descricao: e.target.value } : prev)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Breve descrição do modelo"
                            />
                          </div>

                          {mostrarTitulo && (
                            <div>
                              <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Título padrão do card</label>
                              <input
                                value={draft.titulo || ''}
                                onChange={(e) => setTemplateDraft(prev => prev ? { ...prev, titulo: e.target.value } : prev)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Título que será sugerido ao criar o card"
                              />
                            </div>
                          )}

                          {/* ===== RECORRÊNCIA (criação automática) ===== */}
                          <div className="border-t border-indigo-200 pt-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h4 className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
                                  <ArrowPathIcon className="h-3.5 w-3.5 text-indigo-500" /> Recorrência (criação automática)
                                </h4>
                                <p className="text-[10px] text-gray-500 mt-0.5">O sistema cria este card sozinho, na frequência e horário definidos.</p>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={!!rec?.ativo}
                                onClick={toggleRec}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${rec?.ativo ? 'bg-indigo-600' : 'bg-gray-300'}`}
                                title={rec?.ativo ? 'Desativar recorrência' : 'Ativar recorrência'}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${rec?.ativo ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                            </div>

                            {rec?.ativo && (
                              <div className="mt-3 space-y-3 bg-white rounded-lg border border-gray-200 p-3">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Frequência</label>
                                    <select
                                      value={rec.frequencia}
                                      onChange={(e) => setRec({ frequencia: e.target.value as CardTemplateRecorrencia['frequencia'] })}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                      <option value="diario">Diária</option>
                                      <option value="semanal">Semanal</option>
                                      <option value="mensal">Mensal</option>
                                    </select>
                                  </div>
                                  {rec.frequencia === 'semanal' && (
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Dia da semana</label>
                                      <select
                                        value={rec.diaDaSemana ?? 1}
                                        onChange={(e) => setRec({ diaDaSemana: parseInt(e.target.value, 10) })}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value={0}>Domingo</option>
                                        <option value={1}>Segunda-feira</option>
                                        <option value={2}>Terça-feira</option>
                                        <option value={3}>Quarta-feira</option>
                                        <option value={4}>Quinta-feira</option>
                                        <option value={5}>Sexta-feira</option>
                                        <option value={6}>Sábado</option>
                                      </select>
                                    </div>
                                  )}
                                  {rec.frequencia === 'mensal' && (
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Dia do mês</label>
                                      <input
                                        type="number"
                                        min={1}
                                        max={31}
                                        value={rec.diaDoMes ?? 1}
                                        onChange={(e) => setRec({ diaDoMes: Math.min(31, Math.max(1, parseInt(e.target.value || '1', 10))) })}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Hora</label>
                                    <input
                                      type="number"
                                      min={0}
                                      max={23}
                                      value={rec.hora ?? 9}
                                      onChange={(e) => setRec({ hora: Math.min(23, Math.max(0, parseInt(e.target.value || '0', 10))) })}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] font-medium text-gray-600 mb-1 uppercase tracking-wider">Minuto</label>
                                    <input
                                      type="number"
                                      min={0}
                                      max={59}
                                      value={rec.minuto ?? 0}
                                      onChange={(e) => setRec({ minuto: Math.min(59, Math.max(0, parseInt(e.target.value || '0', 10))) })}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                  </div>
                                </div>
                                {resumoRec && (
                                  <div className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5">
                                    ⏰ {resumoRec} <span className="text-indigo-500">(horário de Brasília)</span>
                                  </div>
                                )}
                                <p className="text-[10px] text-gray-500">
                                  💡 Dica: em campos de data abaixo, escolha uma <strong>data dinâmica</strong> (ex.: "Hoje + 15 dias") para o prazo ser calculado automaticamente a cada criação.
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="border-t border-indigo-200 pt-4">
                            <h4 className="text-xs font-semibold text-gray-800 mb-2">Valores pré-preenchidos</h4>
                            <p className="text-[10px] text-gray-500 mb-3">Deixe em branco para não preencher o campo automaticamente.</p>
                            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                              {campos.map(campo => {
                                const valor = draft.valores?.[campo.id];
                                const naoSuportado = ['anexo', 'documento', 'progresso', 'rotas', 'rota_terrestre', 'justificativa_checkbox', 'texto_informativo'].includes(campo.tipo);

                                return (
                                  <div key={campo.id} className="bg-white rounded-lg border border-gray-200 p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <label className="text-xs font-medium text-gray-700">
                                        {campo.nome}
                                        {campo.obrigatorio && <span className="text-red-500 ml-1">*</span>}
                                      </label>
                                      <span className="text-[9px] text-gray-400">{getTipoNomeAmigavel(campo.tipo)}</span>
                                    </div>

                                    {naoSuportado && (
                                      <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">Este tipo de campo não é suportado em modelos.</p>
                                    )}

                                    {!naoSuportado && (campo.tipo === 'texto' || campo.tipo === 'telefone' || campo.tipo === 'valor') && (
                                      <input
                                        value={valor ?? ''}
                                        onChange={(e) => setDraftValor(campo.id, e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        placeholder="Valor padrão (opcional)"
                                      />
                                    )}

                                    {!naoSuportado && campo.tipo === 'texto_longo' && (
                                      <textarea
                                        value={valor ?? ''}
                                        onChange={(e) => setDraftValor(campo.id, e.target.value)}
                                        rows={3}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        placeholder="Valor padrão (opcional)"
                                      />
                                    )}

                                    {!naoSuportado && (campo.tipo === 'data' || campo.tipo === 'data_vencimento') && (
                                      rec?.ativo ? (
                                        <CampoDataDinamica valor={valor} onChange={(v) => setDraftValor(campo.id, v)} />
                                      ) : (
                                        <input
                                          type="date"
                                          value={valor ?? ''}
                                          onChange={(e) => setDraftValor(campo.id, e.target.value)}
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                      )
                                    )}

                                    {!naoSuportado && (campo.tipo === 'lista' || campo.tipo === 'checkbox_single') && (
                                      <select
                                        value={valor ?? ''}
                                        onChange={(e) => setDraftValor(campo.id, e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">— Não preencher —</option>
                                        {(campo.opcoes || []).map(op => (
                                          <option key={op} value={op}>{op}</option>
                                        ))}
                                      </select>
                                    )}

                                    {!naoSuportado && (campo.tipo === 'checkbox' || campo.tipo === 'checkbox_multi') && (
                                      <div className="flex flex-wrap gap-1.5">
                                        {(campo.opcoes || []).map(op => {
                                          const arr: string[] = Array.isArray(valor) ? valor : [];
                                          const marcado = arr.includes(op);
                                          return (
                                            <button
                                              key={op}
                                              type="button"
                                              onClick={() => {
                                                const novo = marcado ? arr.filter(x => x !== op) : [...arr, op];
                                                setDraftValor(campo.id, novo);
                                              }}
                                              className={`px-2 py-1 text-[11px] rounded-full border transition-colors ${marcado ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                                            >
                                              {op}
                                            </button>
                                          );
                                        })}
                                        {(campo.opcoes || []).length === 0 && (
                                          <p className="text-[10px] text-gray-400">Nenhuma opção configurada no campo.</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-indigo-200">
                            <button type="button" onClick={cancelarDraft} className="px-3 py-1.5 text-xs border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                              Cancelar
                            </button>
                            <button type="button" onClick={salvarDraft} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                              <CheckIcon className="h-3.5 w-3.5" /> Aplicar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}

          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 md:px-6 py-3 border-t border-gray-200 bg-gray-50/80 flex flex-col-reverse sm:flex-row items-center justify-between gap-2">
          {podeExcluirEtapa && canEditStep ? (
            <button onClick={() => onDelete(etapa.id)} type="button" className="flex items-center gap-1.5 px-3 py-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-xs border border-transparent hover:border-red-200 w-full sm:w-auto justify-center sm:justify-start">
              <TrashIcon className="h-4 w-4" />Excluir Etapa
            </button>
          ) : (
            <div className="text-xs text-gray-400 text-center sm:text-left w-full sm:w-auto">
              {!podeExcluirEtapa && "Não é possível excluir etapa com cartões"}
              {podeExcluirEtapa && !canEditStep && "Sem permissão para excluir"}
            </div>
          )}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button onClick={onClose} type="button" className="flex-1 sm:flex-initial px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">Cancelar</button>
            <button onClick={handleSave} type="button" className={`flex-1 sm:flex-initial px-5 py-2 rounded-lg transition-colors text-sm font-medium ${hasAnyEditPermission ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-sm' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={!hasAnyEditPermission}>Salvar</button>
          </div>
        </div>
      </div>

      {/* MODAL IMPORTAR CAMPOS */}
      {modalImportarAberto && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
              <h3 className="text-lg font-bold">Importar Campos</h3>
              <p className="text-blue-100 text-sm mt-1">Selecione um fluxo, etapa e campos para copiar</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {carregandoFluxos ? (
                <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div><p className="text-gray-500">Carregando fluxos...</p></div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">1. Fluxo de origem:</label>
                    <select value={fluxoOrigemImportar} onChange={(e) => { setFluxoOrigemImportar(e.target.value); setEtapaOrigemImportar(''); setCamposSelecionadosImportar([]); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="">-- Selecione --</option>
                      <option value={String(flow.id)}>{flow.name} (fluxo atual)</option>
                      {todosFluxos.filter(f => String(f.id) !== String(flow.id)).map(f => {
                        const total = (f.etapas || []).reduce((a, e) => a + (e.campos?.length || 0), 0);
                        return <option key={f.id} value={String(f.id)}>{f.name} ({f.etapas?.length || 0} etapas, {total} campos)</option>;
                      })}
                    </select>
                  </div>
                  {fluxoOrigemImportar && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">2. Etapa de origem:</label>
                      <select value={etapaOrigemImportar} onChange={(e) => { setEtapaOrigemImportar(e.target.value); setCamposSelecionadosImportar([]); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option value="">-- Selecione --</option>
                        {etapasParaImportar.map(e => <option key={e.id} value={String(e.id)}>{e.nome} ({e.campos?.length || 0} campos)</option>)}
                      </select>
                      {etapasParaImportar.length === 0 && <p className="text-sm text-amber-600 mt-2">Nenhuma etapa com campos disponível</p>}
                    </div>
                  )}
                  {etapaOrigemImportar && (() => {
                    const fluxoOrigem = fluxoOrigemImportar === String(flow.id) ? flow : todosFluxos.find(f => String(f.id) === fluxoOrigemImportar);
                    const etapaOrigem = fluxoOrigem?.etapas?.find(e => String(e.id) === etapaOrigemImportar);
                    if (!etapaOrigem?.campos?.length) return <p className="text-center py-8 text-gray-500">Nenhum campo disponível</p>;
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-medium text-gray-700">3. Campos ({etapaOrigem.campos.length}):</label>
                          <div className="flex gap-2">
                            <button onClick={() => setCamposSelecionadosImportar(etapaOrigem.campos?.map(c => c.id) || [])} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded">Todos</button>
                            <button onClick={() => setCamposSelecionadosImportar([])} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded">Limpar</button>
                          </div>
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto border border-gray-200 rounded-lg p-3">
                          {etapaOrigem.campos.map(campo => {
                            const sel = camposSelecionadosImportar.includes(campo.id);
                            return (
                              <label key={campo.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${sel ? 'bg-blue-50 border-2 border-blue-400' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'}`}>
                                <input type="checkbox" checked={sel} onChange={(e) => { e.target.checked ? setCamposSelecionadosImportar(p => [...p, campo.id]) : setCamposSelecionadosImportar(p => p.filter(id => id !== campo.id)); }} className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 flex items-center gap-2">{campo.nome}{campo.obrigatorio && <span className="text-red-500 text-xs">*</span>}</div>
                                  <div className="text-xs text-gray-500 mt-0.5"><span className="px-1.5 py-0.5 bg-gray-200 rounded">{getTipoNomeAmigavel(campo.tipo)}</span>{campo.opcoes?.length ? ` ${campo.opcoes.length} opções` : ''}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-600">{camposSelecionadosImportar.length > 0 ? `${camposSelecionadosImportar.length} selecionado(s)` : 'Nenhum selecionado'}</span>
              <div className="flex gap-2">
                <button onClick={() => { setModalImportarAberto(false); setFluxoOrigemImportar(''); setEtapaOrigemImportar(''); setCamposSelecionadosImportar([]); }} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs hover:bg-gray-100">Cancelar</button>
                <button onClick={importarCamposSelecionados} disabled={camposSelecionadosImportar.length === 0} className={`px-3 py-1.5 rounded-lg text-xs ${camposSelecionadosImportar.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>Importar{camposSelecionadosImportar.length > 0 ? ` (${camposSelecionadosImportar.length})` : ''}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguracaoEtapa;

